import {
  boolean,
  foreignKey,
  index,
  integer,
  jsonb,
  pgEnum,
  pgPolicy,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
  check,
  numeric,
  decimal,
  real,
  customType,
} from 'drizzle-orm/pg-core';

import { id, timestamps, syncTracking } from './utils';
import { sql } from 'drizzle-orm';
import {
  AGENT_TYPES_VALUES,
  AGENT_CATEGORY_VALUES,
  AGENT_VISIBILITY_VALUES,
  AGENT_MODEL_VALUES,
  AGENT_REASONING_EFFORT_VALUES,
  AGENT_FILE_SETTINGS_VALUES,
  MESSAGE_ROLE_VALUES,
  MEMBER_ROLES_VALUES,
  PROJECT_VISIBILITY_VALUES,
  USER_ROLES_VALUES,
  USER_THEMES_VALUES,
  FILE_STATUS_VALUES,
  STATUS_VALUES,
  TOKEN_USAGE_OPERATIONS,
  FILE_PAGE_STATUS_VALUES,
  WAREHOUSE_SYNC_JOB_STATUS_VALUES,
  WAREHOUSE_SYNC_SESSION_STATUS_VALUES,
  FORECAST_FILE_TYPE_VALUES,
  FORECAST_CONFIG_TARGET_VALUES,
  UPLOAD_SESSION_STATUS_VALUES,
  LIQUID_AC_PATTERN_VALUES,
  ADJUSTMENT_STATUS_VALUES,
  LIQUID_AC_SPEC_RULE_SOURCE_VALUES,
} from '../../data-types';

export const vector = customType<{
  data: number[];
  config?: { length?: number };
}>({
  dataType(config) {
    return `vector(${(config as { length?: number })?.length ?? 3072})`;
  },
});

export const tsvector = customType<{ data: string }>({
  dataType() {
    return 'tsvector';
  },
});

export const userRoleEnum = pgEnum('user_role', USER_ROLES_VALUES);
export const userThemeEnum = pgEnum('user_theme', USER_THEMES_VALUES);
export const statusEnum = pgEnum('status', STATUS_VALUES);

// Canonical Liquid AC v2 enums (ADR-0109). Declared with the other enums so
// every config tier (item overrides AND bid_item_liquid_ac) can reference
// them. The legacy `pattern` varchar column is retained alongside these for
// dual-read/dual-write during the cutover (ADR-0109 R1-R3).
export const liquidAcPatternEnum = pgEnum(
  'liquid_ac_pattern',
  LIQUID_AC_PATTERN_VALUES,
);
export const adjustmentStatusEnum = pgEnum(
  'adjustment_status',
  ADJUSTMENT_STATUS_VALUES,
);

export const users = pgTable(
  'users',
  {
    ...id,
    name: varchar('name').notNull(),
    firstName: varchar('first_name').default(''),
    lastName: varchar('last_name').default(''),
    email: varchar('email').notNull().unique(),
    emailVerified: boolean('email_verified').notNull(),
    image: varchar('image'),
    role: userRoleEnum('role').notNull(),
    theme: userThemeEnum('theme').default('light'),
    debugEnabled: boolean('debug_enabled').notNull().default(false),
    demoEnabled: boolean('demo_enabled').notNull().default(false),
    banned: boolean('banned').notNull().default(false),
    banReason: varchar('ban_reason'),
    banExpires: timestamp('ban_expires'),
    status: statusEnum('status').notNull().default('active'),
    ...timestamps,
  },
  (table) => [index('users_email_idx').on(table.email)],
);

export const sessions = pgTable(
  'sessions',
  {
    ...id,
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, {
        onDelete: 'cascade',
      }),
    expiresAt: timestamp('expires_at').notNull(),
    token: varchar('token').notNull().unique(),
    ipAddress: varchar('ip_address'),
    userAgent: varchar('user_agent'),
    impersonatedBy: uuid('impersonated_by').references(() => users.id),
    ...timestamps,
  },
  (table) => [index().on(table.userId)],
);

export const accounts = pgTable(
  'accounts',
  {
    ...id,
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, {
        onDelete: 'cascade',
      }),
    accountId: varchar('account_id').notNull(),
    providerId: varchar('provider_id').notNull(),
    accessToken: varchar('access_token'),
    refreshToken: varchar('refresh_token'),
    idToken: varchar('id_token'),
    accessTokenExpiresAt: timestamp('access_token_expires_at'),
    refreshTokenExpiresAt: timestamp('refresh_token_expires_at'),
    scope: varchar('scope'),
    password: varchar('password'),
    ...timestamps,
  },
  (table) => [index().on(table.userId)],
);

export const verifications = pgTable('verifications', {
  ...id,
  identifier: varchar('identifier').notNull(),
  value: varchar('value').notNull(),
  expiresAt: timestamp('expires_at').notNull(),
  ...timestamps,
});

export const oauthClients = pgTable(
  'oauthClients',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    clientId: text('client_id').notNull().unique(),
    clientSecret: text('client_secret'),
    disabled: boolean('disabled').default(false),
    skipConsent: boolean('skip_consent'),
    enableEndSession: boolean('enable_end_session'),
    subjectType: text('subject_type'),
    scopes: text('scopes').array(),
    userId: text('user_id'),
    name: text('name'),
    uri: text('uri'),
    icon: text('icon'),
    contacts: text('contacts').array(),
    tos: text('tos'),
    policy: text('policy'),
    softwareId: text('software_id'),
    softwareVersion: text('software_version'),
    softwareStatement: text('software_statement'),
    redirectUris: text('redirect_uris').array().notNull(),
    postLogoutRedirectUris: text('post_logout_redirect_uris').array(),
    tokenEndpointAuthMethod: text('token_endpoint_auth_method'),
    grantTypes: text('grant_types').array(),
    responseTypes: text('response_types').array(),
    public: boolean('public'),
    type: text('type'),
    requirePKCE: boolean('require_pkce'),
    referenceId: text('reference_id'),
    metadata: jsonb('metadata'),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    updatedAt: timestamp('updated_at').notNull().defaultNow(),
  },
  (table) => [
    index('oauth_clients_client_id_idx').on(table.clientId),
    index('oauth_clients_user_id_idx').on(table.userId),
  ],
);

export const oauthRefreshTokens = pgTable(
  'oauthRefreshTokens',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    token: text('token').notNull().unique(),
    clientId: text('client_id').notNull(),
    sessionId: text('session_id'),
    userId: text('user_id').notNull(),
    referenceId: text('reference_id'),
    expiresAt: timestamp('expires_at'),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    revoked: timestamp('revoked'),
    authTime: timestamp('auth_time'),
    scopes: text('scopes').array().notNull(),
  },
  (table) => [
    index('oauth_refresh_tokens_client_id_idx').on(table.clientId),
    index('oauth_refresh_tokens_session_id_idx').on(table.sessionId),
    index('oauth_refresh_tokens_user_id_idx').on(table.userId),
  ],
);

export const oauthAccessTokens = pgTable(
  'oauthAccessTokens',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    token: text('token').unique(),
    clientId: text('client_id').notNull(),
    sessionId: text('session_id'),
    userId: text('user_id'),
    referenceId: text('reference_id'),
    refreshId: text('refresh_id'),
    expiresAt: timestamp('expires_at'),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    scopes: text('scopes').array().notNull(),
  },
  (table) => [
    index('oauth_access_tokens_client_id_idx').on(table.clientId),
    index('oauth_access_tokens_session_id_idx').on(table.sessionId),
    index('oauth_access_tokens_user_id_idx').on(table.userId),
    index('oauth_access_tokens_refresh_id_idx').on(table.refreshId),
  ],
);

export const oauthConsents = pgTable(
  'oauthConsents',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    clientId: text('client_id').notNull(),
    userId: text('user_id'),
    referenceId: text('reference_id'),
    scopes: text('scopes').array().notNull(),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    updatedAt: timestamp('updated_at').notNull().defaultNow(),
  },
  (table) => [
    index('oauth_consents_client_id_idx').on(table.clientId),
    index('oauth_consents_user_id_idx').on(table.userId),
  ],
);

export const jwks = pgTable('jwks', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => crypto.randomUUID()),
  publicKey: text('public_key').notNull(),
  privateKey: text('private_key').notNull(),
  createdAt: timestamp('created_at').notNull().defaultNow(),
  expiresAt: timestamp('expires_at'),
});

export const organizations = pgTable(
  'organizations',
  {
    ...id,
    name: varchar('name').notNull().unique(),
    logo: varchar('logo'),
    metadata: varchar(),
    domains: text('domains').array(),
    usedSeats: integer('used_seats').notNull().default(0),
    maxSeats: integer('max_seats').notNull().default(0),
    maxOCRPagesPerMonth: integer('max_ocr_pages_per_month')
      .notNull()
      .default(10000),
    customBidTabsMonthlySpendCeilingUsd: numeric(
      'custom_bid_tabs_monthly_spend_ceiling_usd',
      { precision: 20, scale: 10 },
    )
      .notNull()
      .default('50.0000000000'),
    isDemo: boolean('is_demo').notNull().default(false),
    warehouseOwnerIds: jsonb('warehouse_owner_ids').$type<string[]>(),
    status: statusEnum('status').notNull().default('active'),
    ...timestamps,
  },
  () => [
    check(
      'organizations_custom_bid_tabs_monthly_spend_ceiling_usd_nonnegative',
      sql`custom_bid_tabs_monthly_spend_ceiling_usd >= 0`,
    ),
  ],
);

export const membersRoleEnum = pgEnum('members_role', MEMBER_ROLES_VALUES);

export const members = pgTable(
  'members',
  {
    ...id,
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, {
        onDelete: 'cascade',
      }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, {
        onDelete: 'cascade',
      }),
    role: membersRoleEnum('role').notNull(),
    ...timestamps,
  },
  (table) => [
    index('members_user_id_idx').on(table.userId),
    index('members_organization_id_idx').on(table.organizationId),
  ],
);

export const agentVisibilityEnum = pgEnum(
  'agent_visibility',
  AGENT_VISIBILITY_VALUES,
);

export const agentTypeEnum = pgEnum('agent_type', AGENT_TYPES_VALUES);
export const agentCategoryEnum = pgEnum(
  'agent_category',
  AGENT_CATEGORY_VALUES,
);
export const agentModelEnum = pgEnum('agent_model', AGENT_MODEL_VALUES);
export const agentReasoningEffortEnum = pgEnum(
  'agent_reasoning_effort',
  AGENT_REASONING_EFFORT_VALUES,
);
export const agentFileSettingsEnum = pgEnum(
  'agent_file_settings',
  AGENT_FILE_SETTINGS_VALUES,
);

export const messageRoleEnum = pgEnum('message_role', MESSAGE_ROLE_VALUES);

export const agents = pgTable(
  'agents',
  {
    ...id,
    name: varchar('name').notNull(),
    description: varchar('description', { length: 2048 }),
    userId: uuid('user_id').references(() => users.id, {
      onDelete: 'restrict',
    }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'restrict',
    }),
    visibility: agentVisibilityEnum('visibility').notNull().default('public'),
    type: agentTypeEnum('type').notNull().default('custom'),
    category: agentCategoryEnum('category')
      .notNull()
      .default('document_analysis'),
    agentKey: varchar('agent_key'),
    userPrompt: text('user_prompt'),
    systemPrompt: text('system_prompt'),
    model: agentModelEnum('model').default('gpt-5'),
    temperature: real('temperature').default(0.0),
    presencePenalty: real('presence_penalty').default(0.0),
    frequencyPenalty: real('frequency_penalty').default(0.0),
    maxRetries: integer('max_retries').default(2),
    maxSteps: integer('max_steps').default(8),
    maxOutputTokens: integer('max_output_tokens'),
    reasoningEnabled: boolean('reasoning_enabled').default(false),
    reasoningEffort:
      agentReasoningEffortEnum('reasoning_effort').default('medium'),
    enabledTools: text('enabled_tools').array().default(sql`ARRAY[]::text[]`),
    toolPolicy: jsonb('tool_policy').$type<{
      categories?: string[];
      tags?: string[];
      includeIds?: string[];
      excludeIds?: string[];
    } | null>(),
    toolChoice: varchar('tool_choice').default('auto'),
    stopOnToolCalled: varchar('stop_on_tool_called'),
    fileSettings: agentFileSettingsEnum('file_settings')
      .notNull()
      .default('none'),
    enabledOrganizationIds: jsonb('enabled_organization_ids')
      .notNull()
      .default(sql`'[]'::jsonb`)
      .$type<string[]>(),
    beta: boolean('beta').notNull().default(false),
    isDefault: boolean('is_default').notNull().default(false),
    ...timestamps,
  },
  (table) => [
    index().on(table.userId),
    index().on(table.organizationId),
    index('agents_enabled_org_ids_gin_index').using(
      'gin',
      table.enabledOrganizationIds,
    ),
    // At most one platform-level default per category — the FAB resolves
    // "the general agent" by (category, isDefault, organizationId IS NULL).
    uniqueIndex('agents_category_default_unique')
      .on(table.category)
      .where(sql`is_default = true AND organization_id IS NULL`),
    check(
      'agents_type_matches_organization',
      sql`(
      (organization_id IS NULL AND type = 'default') OR
      (organization_id IS NOT NULL AND type = 'custom')
    )`,
    ),
    check('check_temperature', sql`temperature >= 0 AND temperature <= 2`),
    check(
      'check_presence_penalty',
      sql`presence_penalty >= -2 AND presence_penalty <= 2`,
    ),
    check(
      'check_frequency_penalty',
      sql`frequency_penalty >= -2 AND frequency_penalty <= 2`,
    ),
    check('check_max_retries', sql`max_retries >= 0 AND max_retries <= 5`),
    check('check_max_steps', sql`max_steps >= 1 AND max_steps <= 100`),
    check(
      'check_max_output_tokens',
      sql`max_output_tokens IS NULL OR (max_output_tokens >= 1 AND max_output_tokens <= 128000)`,
    ),
    // Tool names are validated at runtime against the @edgevanta/agent-tools
    // registry, not via a DB check constraint. Adding a new tool no longer
    // requires a migration — drop in a file, add to the registry, done.
    //
    // Platform-default agents MUST point at a disk config via agent_key.
    // The chat layer overlays the file-based generalist/specialist config
    // on top of the DB row at runtime, so an unkeyed default would
    // silently fall back to whatever stale enabled_tools / system_prompt
    // happen to be in the row — the exact regression that wiped tools
    // after a DB reset. This check makes that class of bug impossible.
    check(
      'check_default_agent_has_agent_key',
      sql`is_default = false OR agent_key IS NOT NULL`,
    ),
  ],
);

export const threads = pgTable(
  'threads',
  {
    ...id,
    title: varchar('title').notNull(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, {
        onDelete: 'restrict',
      }),
    projectId: uuid('project_id').references(() => projects.id, {
      onDelete: 'cascade',
    }),
    agentId: uuid('agent_id')
      .notNull()
      .references(() => agents.id, {
        onDelete: 'restrict',
      }),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, {
        onDelete: 'restrict',
      }),
    ...timestamps,
  },
  (table) => [
    index().on(table.userId),
    index().on(table.agentId),
    index().on(table.organizationId),
    // Composite-FK target so child rows (messages, thread_files) can pin the
    // parent thread's org to their own org (cross-tenant-FK hardening).
    unique('threads_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
  ],
);

// Lifecycle of an assistant message generation:
//   pending   — job enqueued, worker hasn't started yet
//   streaming — worker actively pushing chunks to pubsub + DB
//   done      — final part persisted; safe to render terminal state
//   error     — generation failed; metadata.error carries the reason
// User messages skip pending/streaming and are written as done directly.
export const messageStatusEnum = pgEnum('message_status', [
  'pending',
  'streaming',
  'done',
  'error',
]);

// Named because application code matches on it: a turn outliving a deleted
// thread must be told apart from a message aimed at another org's thread, and
// the two differ only by this constraint name on an otherwise identical 23503.
export const MESSAGES_THREAD_ORGANIZATION_FK =
  'messages_thread_id_organization_id_fk';

export const messages = pgTable(
  'messages',
  {
    ...id,
    threadId: uuid('thread_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    uiMessageId: varchar('ui_message_id').notNull(),
    // Sender of this message. Threads are org-shared, so the thread owner is
    // not necessarily who wrote a given message. Null for assistant/system
    // rows (no human author) and set null on user deletion to keep history.
    userId: uuid('user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    role: messageRoleEnum('role').notNull(),
    status: messageStatusEnum('status').notNull().default('done'),
    metadata: jsonb('metadata').default('{}'),
    parts: jsonb('parts').notNull().default('[]'),
    ...timestamps,
  },
  (table) => [
    index().on(table.threadId),
    index().on(table.organizationId),
    index().on(table.userId),
    // Powers the "which threads are still working?" sidebar query —
    // partial index keeps it tiny since `done` is the steady state.
    index('messages_active_status_idx')
      .on(table.threadId, table.status)
      .where(sql`status IN ('pending', 'streaming')`),
    uniqueIndex('messages_ui_message_id_thread_id_unique').on(
      table.uiMessageId,
      table.threadId,
    ),
    // Composite FK pinning the parent thread's org to this row's org — a
    // message stamped org A cannot reference org B's thread. Replaces the simple
    // single-column thread_id FK; ON DELETE cascade preserved.
    foreignKey({
      columns: [table.threadId, table.organizationId],
      foreignColumns: [threads.id, threads.organizationId],
      name: MESSAGES_THREAD_ORGANIZATION_FK,
    }).onDelete('cascade'),
  ],
);

// Internal QA signal: per-message thumbs-up / thumbs-down. Captured to
// catch agent regressions (hallucinations, wrong calculations, swallowed
// errors) before they reach Mason / Tristan. One row per (message, user)
// — re-rating the same message overwrites via the unique index.
export const messageRatingEnum = pgEnum('message_rating', ['good', 'bad']);

export const messageFeedback = pgTable(
  'message_feedback',
  {
    ...id,
    messageId: uuid('message_id')
      .notNull()
      .references(() => messages.id, { onDelete: 'cascade' }),
    threadId: uuid('thread_id')
      .notNull()
      .references(() => threads.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'restrict' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    rating: messageRatingEnum('rating').notNull(),
    comment: text('comment'),
    ...timestamps,
  },
  (table) => [
    index().on(table.messageId),
    index().on(table.threadId),
    index().on(table.organizationId),
    index().on(table.rating),
    uniqueIndex('message_feedback_message_user_unique').on(
      table.messageId,
      table.userId,
    ),
  ],
);

export const fileStatusEnum = pgEnum('file_status', FILE_STATUS_VALUES);
export const filePageStatusEnum = pgEnum(
  'file_page_status',
  FILE_PAGE_STATUS_VALUES,
);

export const projectVisibilityEnum = pgEnum(
  'project_visibility',
  PROJECT_VISIBILITY_VALUES,
);

export const projects = pgTable(
  'projects',
  {
    ...id,
    name: varchar('name', { length: 255 }).notNull(),
    description: varchar('description', { length: 2048 }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, {
        onDelete: 'restrict',
      })
      .notNull(),
    visibility: projectVisibilityEnum('visibility').notNull().default('public'),
    ...timestamps,
  },
  (table) => [
    index('projects_user_id_idx').on(table.userId),
    // Composite-FK target so project_files can pin the parent project's org.
    unique('projects_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
  ],
);

export const uploadSessionStatusEnum = pgEnum(
  'upload_session_status',
  UPLOAD_SESSION_STATUS_VALUES,
);

export const uploadSessions = pgTable(
  'upload_sessions',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, {
        onDelete: 'restrict',
      })
      .notNull(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    name: varchar('name', { length: 255 }).notNull(),
    // Old code omits this value; true keeps those sessions on the bulk queue
    // while new code always writes isBulk explicitly.
    isBulk: boolean('is_bulk').notNull().default(true),
    status: uploadSessionStatusEnum('status').notNull().default('processing'),
    fileCount: integer('file_count').notNull().default(0),
    formatSummary: jsonb('format_summary')
      .$type<Record<string, number>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    ...timestamps,
  },
  (table) => [
    index('upload_sessions_organization_id_idx').on(table.organizationId),
    index('upload_sessions_organization_id_created_at_idx').on(
      table.organizationId,
      table.createdAt.desc(),
    ),
  ],
);

export const files = pgTable(
  'files',
  {
    ...id,
    storageId: uuid('storage_id').notNull().defaultRandom(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, {
        onDelete: 'restrict',
      })
      .notNull(),
    key: varchar('key', { length: 1024 }).notNull(),
    bucket: varchar('bucket', { length: 255 }).notNull(),
    fileName: varchar('file_name', { length: 255 }).notNull(),
    mimeType: varchar('mime_type', { length: 100 }),
    format: varchar('format', { length: 20 }),
    contentHash: varchar('content_hash', { length: 64 }),
    uploadSessionId: uuid('upload_session_id').references(
      () => uploadSessions.id,
      { onDelete: 'cascade' },
    ),
    status: fileStatusEnum('status').notNull().default('queued'),
    errorMessage: varchar('error_message', { length: 100 }),
    openaiFileId: varchar('openai_file_id'),
    metadata: jsonb('metadata')
      .$type<{
        sessionId?: string;
        bidTabBackfill?: boolean;
        numberOfPages?: number;
        processedPages?: number;
        ocrPages?: number;
        analysis?: {
          type: 'text' | 'mixed' | 'scanned';
          avgMbPerPage: number;
          estimatedMemoryPerPageMb: number;
          estimatedTotalMemoryGb: number;
          shouldUseThumbnailFlow: boolean;
          analyzedAt: string;
        };
        error?: {
          message: string;
          stack?: string;
          timestamp: string;
        };
        /** The PDF a DOC/DOCX upload was converted to, produced once and owned
         *  by this file. Extraction, retries, thumbnails, and Preview all read
         *  these exact bytes so their pagination can never diverge. Absent
         *  until the conversion has been stored and verified. */
        convertedPdf?: {
          key: string;
          bytes: number;
          sha256: string;
          sourceFormat: 'docx';
          convertedAt: string;
        };
      }>()
      .default(sql`'{}'::jsonb`),
    qstashJobIds: jsonb('qstash_job_ids')
      .$type<string[]>()
      .default(sql`'[]'::jsonb`),
    ...timestamps,
  },
  (table) => [
    index('files_user_id_idx').on(table.userId),
    index('files_organization_id_content_hash_idx').on(
      table.organizationId,
      table.contentHash,
    ),
    index('files_upload_session_id_idx').on(table.uploadSessionId),
    // Composite-FK target so child rows (extraction sessions) can pin the
    // parent file's org to their own org (cross-tenant-FK hardening).
    unique('files_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
  ],
);

export const filePages = pgTable(
  'file_pages',
  {
    ...id,
    fileId: uuid('file_id')
      .notNull()
      .references(() => files.id, { onDelete: 'cascade' }),
    pageNumber: integer('page_number').notNull(),
    filePath: varchar('file_path', { length: 1024 }),
    thumbnailPath: varchar('thumbnail_path', { length: 1024 }).unique(),
    hasText: boolean('has_text').notNull().default(false),
    isOcr: boolean('is_ocr').notNull().default(false),
    status: filePageStatusEnum('status').notNull().default('pending'),
    errorMessage: varchar('error_message', { length: 1024 }),
    metadata: jsonb('metadata').$type<Record<string, unknown>>().default({}),
    ...timestamps,
  },
  (table) => [
    index('file_pages_file_id_idx').on(table.fileId),
    index('file_pages_status_idx').on(table.status),
    uniqueIndex('file_pages_file_id_page_number_unique').on(
      table.fileId,
      table.pageNumber,
    ),
  ],
);

export const threadFiles = pgTable(
  'thread_files',
  {
    threadId: uuid('thread_id').notNull(),
    fileId: uuid('file_id')
      .notNull()
      .references(() => files.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id').notNull(), // guard
    // optional metadata
    attachedAt: timestamp('attached_at', { withTimezone: true }).defaultNow(),
    attachedBy: uuid('attached_by').references(() => users.id, {
      onDelete: 'set null',
    }),
    isIndexed: boolean('is_indexed').notNull().default(false),
  },
  (t) => [
    uniqueIndex('thread_files_thread_id_file_id_unique').on(
      t.threadId,
      t.fileId,
    ),
    index('thread_files_thread_id_idx').on(t.threadId),
    index('thread_files_file_id_idx').on(t.fileId),
    index('thread_files_org_id_idx').on(t.organizationId),
    // Composite FK pinning the parent thread's org to this row's org — a link
    // stamped org A cannot reference org B's thread. Replaces the single-column
    // thread_id FK; ON DELETE cascade preserved.
    foreignKey({
      columns: [t.threadId, t.organizationId],
      foreignColumns: [threads.id, threads.organizationId],
      name: 'thread_files_thread_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const projectFiles = pgTable(
  'project_files',
  {
    projectId: uuid('project_id').notNull(),
    fileId: uuid('file_id')
      .notNull()
      .references(() => files.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id').notNull(), // guard
    attachedAt: timestamp('attached_at', { withTimezone: true }).defaultNow(),
    attachedBy: uuid('attached_by').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (t) => [
    uniqueIndex('project_files_file_id_unique').on(t.fileId, t.projectId),
    index('project_files_project_id_idx').on(t.projectId),
    index('project_files_org_id_idx').on(t.organizationId),
    // Composite FK pinning the parent project's org to this row's org. Replaces
    // the single-column project_id FK; ON DELETE cascade preserved.
    foreignKey({
      columns: [t.projectId, t.organizationId],
      foreignColumns: [projects.id, projects.organizationId],
      name: 'project_files_project_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const owners = pgTable(
  'owners',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    name: varchar('name', { length: 255 }).notNull(),
    // An owner ALWAYS has a state — required for both DOT/warehouse-synced and
    // tenant custom owners. A stateless owner is invisible in the custom-bid
    // Details picker (gated by state) yet linkable, which corrupts the flow.
    stateId: uuid('state_id').notNull(),
    // NULL = DOT/system-sourced owner (shared via organization_owners).
    // Set = tenant-owned custom owner, scoped to a single organization.
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('owners_external_id_unique').on(table.externalId),
    index('owners_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
  ],
);

export const organizationOwners = pgTable(
  'organization_owners',
  {
    ...id,
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('organization_owners_org_owner_unique').on(
      table.organizationId,
      table.ownerId,
    ),
    index('organization_owners_owner_id_idx').on(table.ownerId),
  ],
);

export const states = pgTable(
  'states',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }).notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    abbreviation: varchar('abbreviation', { length: 10 }).notNull(),
    ...timestamps,
  },
  (table) => [uniqueIndex('states_external_id_unique').on(table.externalId)],
);

export const globalVendors = pgTable(
  'global_vendors',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    globalVendorName: varchar('global_vendor_name', { length: 512 }).notNull(),
    globalVendorNameSearch: tsvector(
      'global_vendor_name_search',
    ).generatedAlwaysAs(
      sql`to_tsvector('simple', COALESCE(global_vendor_name, ''))`,
    ),
    globalNormalizedName: varchar('global_normalized_name', { length: 512 }),
    // NULL = DOT/system-synced canonical vendor (shared by every org).
    // Set = a custom global minted by that org's custom-bid-tab imports,
    // private to it. Mirrors owners' NULL-shared / set-org-custom convention.
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('global_vendors_external_id_unique').on(table.externalId),
    index('global_vendors_name_search_idx').using(
      'gin',
      table.globalVendorNameSearch,
    ),
    index('global_vendors_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
  ],
);

export const vendors = pgTable(
  'vendors',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    ownerVendorName: varchar('owner_vendor_name', { length: 255 }).notNull(),
    ownerVendorNameSearch: tsvector(
      'owner_vendor_name_search',
    ).generatedAlwaysAs(
      sql`to_tsvector('simple', COALESCE(owner_vendor_name, ''))`,
    ),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    globalVendorId: uuid('global_vendor_id').references(
      () => globalVendors.id,
      { onDelete: 'set null' },
    ),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('vendors_owner_id_idx').on(table.ownerId),
    index('vendors_owner_vendor_name_search_idx').using(
      'gin',
      table.ownerVendorNameSearch,
    ),
    uniqueIndex('vendors_external_id_unique').on(table.externalId),
    index('vendors_global_vendor_id_idx').on(table.globalVendorId),
    index('vendors_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
    index('vendors_deleted_at_idx')
      .on(table.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

export const lettings = pgTable(
  'lettings',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    date: timestamp('date').notNull(),
    proposalCount: integer('proposal_count').notNull().default(0),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('lettings_owner_id_idx').on(table.ownerId),
    uniqueIndex('lettings_external_id_unique').on(table.externalId),
    index('lettings_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
    index('lettings_deleted_at_idx')
      .on(table.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

export const items = pgTable(
  'items',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    number: varchar('number', { length: 255 }).notNull(),
    // Space-stripped form of `number`, stored so catalog-match lookups compare
    // with a plain `=` (leakproof → index-usable under RLS). The legacy
    // expression index on replace(number,' ','') becomes unusable once RLS is
    // active because replace() is not leakproof; this column avoids that. Plain
    // column (NOT generated) kept current by the items_number_normalized_sync
    // trigger and populated by an in-migration backfill.
    numberNormalized: text('number_normalized'),
    ownerItemDescription: text('owner_item_description'),
    ownerItemDescriptionSearch: tsvector(
      'owner_item_description_search',
    ).generatedAlwaysAs(
      sql`to_tsvector('simple', COALESCE(owner_item_description, ''))`,
    ),
    categoryId: uuid('category_id').references(() => itemCategories.id, {
      onDelete: 'set null',
    }),
    unit: varchar('unit', { length: 50 }),
    unitId: uuid('unit_id').references(() => units.id, {
      onDelete: 'set null',
    }),
    isTrueLumpSum: boolean('is_true_lump_sum').default(false).notNull(),
    isCatchAll: boolean('is_catch_all').default(false).notNull(),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('items_owner_id_idx').on(table.ownerId),
    index('items_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
    index('items_owner_item_description_search_idx').using(
      'gin',
      table.ownerItemDescriptionSearch,
    ),
    index('items_description_trgm_idx').using(
      'gin',
      sql`${table.ownerItemDescription} gin_trgm_ops`,
    ),
    uniqueIndex('items_external_id_unique').on(table.externalId),
    index('items_number_owner_id_idx').on(table.number, table.ownerId),
    // The leakproof, RLS-usable replacement for the legacy replace(number,' ','')
    // expression index (0213). Every items-side catalog-match query compares
    // number_normalized with a plain `=` as of this PR; bid_items gets its own
    // normalized column in a follow-up.
    index('items_number_normalized_owner_id_idx')
      .on(table.numberNormalized, table.ownerId)
      .where(sql`deleted_at IS NULL`),
    // Partial index over soft-deleted rows only — the purge scans
    // `WHERE deleted_at IS NOT NULL`, so without this the tail chunks
    // sequential-scan the whole table and time out.
    index('items_deleted_at_idx')
      .on(table.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

/**
 * Review lifecycle for extracted (custom) bid-tab entities. NULL means the row
 * is not subject to review (DOT/system data) and is always available; custom
 * extractions start 'pending' and stay hidden from the app until 'approved'.
 */
export const extractionReviewStatusEnum = pgEnum('extraction_review_status', [
  'pending',
  'approved',
  'rejected',
]);

// A bid's AWARD STATUS (EDG-1588). Mirrors the extraction engine's `BidStatus`
// vocabulary EXACTLY. 'valid' (the default) competes for the award; every other
// value is printed on the sheet but NOT awardable — it keeps its price/total but
// is excluded from the ranking. Extraction/UI set the invalid values; the
// publish path writes them onto the bid.
export const bidStatusEnum = pgEnum('bid_status', [
  'valid',
  'irregular',
  'non_responsive',
  'withdrawn',
  'rejected',
  'no_bid',
]);

/**
 * Machine normalization state of a custom proposal (orthogonal to review_status,
 * which is the human approve/reject axis). `normalized` = tail complete or nothing
 * to defer; `awaiting_owner` = extracted but no owner in the file, owner-gated tail
 * deferred until the user picks/creates an owner; `normalizing` = owner set, the
 * owner-gated tail (items/vendors/classification) is running in the background and
 * the matrix is locked.
 */
export const customProposalNormalizationStatusEnum = pgEnum(
  'custom_proposal_normalization_status',
  ['normalized', 'awaiting_owner', 'normalizing'],
);

/**
 * The schedule a proposal line belongs to within a bid tab (EDG-755). A bid tab
 * prints labeled groups — BASE BID, ADDITIVE ALTERNATE NO. 1, SUPPLEMENTAL,
 * ALLOWANCE — each with its own per-vendor total; `base` is the default and the
 * only group historic (DOT/warehouse) rows have, so every existing line reads as
 * base bid. Mirrors the extraction model's `BidGroupSchema.type`
 * (packages/bid-tab-extract/src/model/bid-tabulation.ts).
 */
export const bidGroupTypeEnum = pgEnum('bid_group_type', [
  'base',
  'supplemental',
  'alternate',
  'allowance',
  // EDG-1583: the blueprint-directed engine distinguishes an alternate that ADDS
  // to the base bid from one that SUBTRACTS. `alternate` (above) stays for
  // warehouse/DOT rows that never carried the sign; custom bid tabs publish the
  // real kind so the awarded selection can add or deduct correctly.
  'additive_alternate',
  'deductive_alternate',
]);

export const proposals = pgTable(
  'proposals',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    callNumber: varchar('call_number', { length: 255 }).notNull(),
    completionCalendarDays: integer('completion_calendar_days'),
    completionDate: timestamp('completion_date'),
    // A deduced date is a FLOOR, not a fact: it excludes holidays, non-charged
    // weather days and the letting→NTP lag, so it reads early and never late.
    // Branch on this flag, never on the source string.
    completionDateIsDeduced: boolean('completion_date_is_deduced')
      .notNull()
      .default(false),
    completionDateSource: varchar('completion_date_source', { length: 255 }),
    completionWorkingDays: integer('completion_working_days'),
    contractId: varchar('contract_id', { length: 255 }),
    // Both axes are proposal-level: exactly one of each per proposal. Classified
    // in the warehouse (delivery method / work kind), not entered by a tenant.
    contractTypeId: uuid('contract_type_id').references(
      () => contractTypes.id,
      { onDelete: 'set null' },
    ),
    projectTypeId: uuid('project_type_id').references(() => projectTypes.id, {
      onDelete: 'set null',
    }),
    costEstimate: numeric('cost_estimate', {
      precision: 20,
      scale: 10,
    }),
    description: text('description'),
    lettingDate: timestamp('letting_date'),
    projectNumber: varchar('project_number', { length: 255 }),
    lettingId: uuid('letting_id')
      .notNull()
      .references(() => lettings.id, { onDelete: 'cascade' }),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    route: varchar('route', { length: 255 }),
    bidderCount: integer('bidder_count'),
    hasAwarded: boolean('has_awarded').notNull().default(false),
    hasBidTabulation: boolean('has_bid_tabulation').notNull().default(false),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    // Review gating for extracted custom bid tabs (EDG-1178). NULL = DOT/system
    // row, always available; custom extractions are 'pending' until reviewed and
    // stay hidden from the app unless 'approved'. Lettings inherit availability
    // from their proposals, so the status lives only here.
    reviewStatus: extractionReviewStatusEnum('review_status'),
    reviewedAt: timestamp('reviewed_at', { mode: 'date' }),
    reviewedBy: uuid('reviewed_by').references(() => users.id, {
      onDelete: 'set null',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('proposals_letting_id_idx').on(table.lettingId),
    index('proposals_owner_id_idx').on(table.ownerId),
    uniqueIndex('proposals_external_id_unique').on(table.externalId),
    index('proposals_owner_contract_idx').on(table.ownerId, table.contractId),
    index('proposals_contract_type_id_idx').on(table.contractTypeId),
    index('proposals_project_type_id_idx').on(table.projectTypeId),
    index('proposals_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
    // Partial index over custom (under-review) rows only — DOT rows are NULL.
    index('proposals_review_status_idx')
      .on(table.reviewStatus)
      .where(sql`review_status IS NOT NULL`),
    index('proposals_deleted_at_idx')
      .on(table.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

export const proposalExceptions = pgTable(
  'proposal_exceptions',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'cascade' }),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    code: varchar('code', { length: 255 }).notNull(),
    fields: text('fields'),
    reason: text('reason'),
    exceptionLevel: varchar('exception_level', { length: 50 }),
    approvalStatus: varchar('approval_status', { length: 50 }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('proposal_exceptions_proposal_id_idx').on(table.proposalId),
    index('proposal_exceptions_owner_id_idx').on(table.ownerId),
    uniqueIndex('proposal_exceptions_external_id_unique').on(table.externalId),
    index('proposal_exceptions_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
  ],
);

export const counties = pgTable(
  'counties',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }).notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    stateId: uuid('state_id').references(() => states.id, {
      onDelete: 'set null',
    }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('counties_external_id_unique').on(table.externalId),
    index('counties_state_id_idx').on(table.stateId),
  ],
);

export const proposalCounties = pgTable(
  'proposal_counties',
  {
    ...id,
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'cascade' }),
    countyId: uuid('county_id')
      .notNull()
      .references(() => counties.id, { onDelete: 'cascade' }),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('proposal_counties_proposal_id_idx').on(table.proposalId),
    index('proposal_counties_county_id_idx').on(table.countyId),
    index('proposal_counties_owner_id_idx').on(table.ownerId),
    uniqueIndex('proposal_counties_proposal_id_county_id_unique').on(
      table.proposalId,
      table.countyId,
    ),
    index('proposal_counties_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
  ],
);

export const districts = pgTable(
  'districts',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }).notNull(),
    code: varchar('code', { length: 255 }).notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    label: varchar('label', { length: 255 }),
    stateId: uuid('state_id').references(() => states.id, {
      onDelete: 'set null',
    }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('districts_external_id_unique').on(table.externalId),
    index('districts_state_id_idx').on(table.stateId),
  ],
);

export const districtCounties = pgTable(
  'district_counties',
  {
    ...id,
    districtId: uuid('district_id')
      .notNull()
      .references(() => districts.id, { onDelete: 'cascade' }),
    countyId: uuid('county_id')
      .notNull()
      .references(() => counties.id, { onDelete: 'cascade' }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('district_counties_district_id_idx').on(table.districtId),
    index('district_counties_county_id_idx').on(table.countyId),
    uniqueIndex('district_counties_district_id_county_id_unique').on(
      table.districtId,
      table.countyId,
    ),
  ],
);

export const proposalDistricts = pgTable(
  'proposal_districts',
  {
    ...id,
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'cascade' }),
    districtId: uuid('district_id')
      .notNull()
      .references(() => districts.id, { onDelete: 'cascade' }),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    source: varchar('source', { length: 50 }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('proposal_districts_proposal_id_idx').on(table.proposalId),
    index('proposal_districts_district_id_idx').on(table.districtId),
    index('proposal_districts_owner_id_idx').on(table.ownerId),
    uniqueIndex('proposal_districts_proposal_id_district_id_unique').on(
      table.proposalId,
      table.districtId,
    ),
    index('proposal_districts_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
  ],
);

export const proposalItems = pgTable(
  'proposal_items',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'cascade' }),
    itemId: uuid('item_id')
      .notNull()
      .references(() => items.id, { onDelete: 'cascade' }),
    lineNumber: integer('line_number'),
    itemNumber: varchar('item_number', { length: 255 }),
    quantity: numeric('quantity', { precision: 20, scale: 10 }).notNull(),
    costEstimate: numeric('cost_estimate', {
      precision: 20,
      scale: 10,
    }),
    proposalItemDescription: text('proposal_item_description'),
    proposalItemDescriptionSearch: tsvector(
      'proposal_item_description_search',
    ).generatedAlwaysAs(
      sql`to_tsvector('simple', COALESCE(proposal_item_description, ''))`,
    ),
    // The bid-tab schedule this line belongs to (EDG-755). `bid_group_type`
    // drives the award math: a custom bid tab (EDG-1583) publishes the signed
    // kind (`additive_alternate` / `deductive_alternate`), and only the base plus
    // the AWARDED alternates enter the comparison/adjusted total + ranking. It
    // defaults to 'base' so every pre-existing line reads as base bid;
    // `bid_group_name` is the printed label and the grouping key within a
    // proposal ('ADDITIVE ALTERNATE NO. 1' vs 'NO. 2'), null for the base group.
    bidGroupType: bidGroupTypeEnum('bid_group_type').notNull().default('base'),
    bidGroupName: varchar('bid_group_name', { length: 255 }),
    // EDG-1583: whether this line's alternate group is AWARDED — the owner's
    // accept decision, carried from the Price sheet's award toggle. Base lines
    // are always in the total, so this only means anything on an alternate; it
    // drives the comparison/adjusted total (awarded additive add, awarded
    // deductive subtract) and the awarded badge in the results view.
    awarded: boolean('awarded').notNull().default(false),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('proposal_items_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
    index('proposal_items_proposal_id_idx').on(table.proposalId),
    index('proposal_items_item_id_idx').on(table.itemId),
    index('proposal_items_owner_id_idx').on(table.ownerId),
    index('proposal_items_proposal_item_description_search_idx').using(
      'gin',
      table.proposalItemDescriptionSearch,
    ),
    uniqueIndex('proposal_items_proposal_id_item_number_line_number_unique').on(
      table.proposalId,
      table.itemNumber,
      table.lineNumber,
    ),
    uniqueIndex('proposal_items_external_id_unique').on(table.externalId),
    index('proposal_items_item_number_proposal_idx').on(
      table.itemNumber,
      table.proposalId,
    ),
  ],
);

export const bids = pgTable(
  'bids',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    vendorId: uuid('vendor_id')
      .notNull()
      .references(() => vendors.id, { onDelete: 'cascade' }),
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'cascade' }),
    proposalVendorName: varchar('proposal_vendor_name', { length: 255 }),
    proposalVendorNameSearch: tsvector(
      'proposal_vendor_name_search',
    ).generatedAlwaysAs(
      sql`to_tsvector('simple', COALESCE(proposal_vendor_name, ''))`,
    ),
    deviation: numeric('deviation', { precision: 20, scale: 10 }),
    value: numeric('value', { precision: 20, scale: 10 }).notNull(),
    winner: boolean('winner').default(false),
    rank: integer('rank'),
    extensionTotal: numeric('extension_total', { precision: 20, scale: 10 }),
    timeAdjustment: numeric('time_adjustment', { precision: 20, scale: 10 }),
    evaluationAdjustment: numeric('evaluation_adjustment', {
      precision: 20,
      scale: 10,
    }),
    adjustedTotal: numeric('adjusted_total', { precision: 20, scale: 10 }),
    deviationPercent: numeric('deviation_percent', { precision: 10, scale: 4 }),
    // The bid's AWARD STATUS (EDG-1588). Defaults to 'valid'; an invalid bid
    // (withdrawn/rejected/non_responsive/irregular/no_bid) keeps its value but is
    // excluded from the ranking. Only the custom-bid publish path sets it today;
    // warehouse-synced bids stay 'valid'.
    status: bidStatusEnum('status').notNull().default('valid'),
    // The printed reason a bid is not 'valid', verbatim. NULL when valid.
    statusReason: text('status_reason'),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('bids_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
    index('bids_vendor_id_idx').on(table.vendorId),
    index('bids_proposal_id_idx').on(table.proposalId),
    index('bids_owner_id_idx').on(table.ownerId),
    index('bids_proposal_vendor_name_search_idx').using(
      'gin',
      table.proposalVendorNameSearch,
    ),
    uniqueIndex('bids_proposal_id_vendor_id_unique').on(
      table.proposalId,
      table.vendorId,
    ),
    index('bids_proposal_value_idx').on(table.proposalId, table.value),
    uniqueIndex('bids_external_id_unique').on(table.externalId),
  ],
);

export const bidItems = pgTable(
  'bid_items',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    bidId: uuid('bid_id')
      .notNull()
      .references(() => bids.id, { onDelete: 'cascade' }),
    proposalItemId: uuid('proposal_item_id')
      .notNull()
      .references(() => proposalItems.id, { onDelete: 'cascade' }),
    quantity: numeric('quantity', { precision: 20, scale: 10 }).notNull(),
    lineNumber: integer('line_number').notNull(),
    unitPrice: numeric('unit_price', { precision: 20, scale: 10 }).notNull(),
    extension: numeric('extension', { precision: 20, scale: 10 }).notNull(),
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, { onDelete: 'cascade' }),
    itemNumber: varchar('item_number', { length: 255 }).notNull(),
    // Plain space-stripped lookup key, mirroring items.number_normalized so
    // tenant/RLS queries can compare with `=` instead of a non-leakproof
    // expression predicate on item_number.
    itemNumberNormalized: text('item_number_normalized'),
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'cascade' }),
    lettingId: uuid('letting_id')
      .notNull()
      .references(() => lettings.id, { onDelete: 'cascade' }),
    bidValue: numeric('bid_value', { precision: 20, scale: 10 }).notNull(),
    bidRank: integer('bid_rank'),
    bidWinner: boolean('bid_winner').default(false),
    bidderCount: integer('bidder_count'),
    lettingDate: timestamp('letting_date'),
    districtName: varchar('district_name', { length: 255 }),
    vendorId: uuid('vendor_id').references(() => vendors.id, {
      onDelete: 'cascade',
    }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('bid_items_bid_id_proposal_item_id_unique').on(
      table.bidId,
      table.proposalItemId,
    ),
    uniqueIndex('bid_items_external_id_unique').on(table.externalId),
    index('bid_items_proposal_id_idx').on(table.proposalId),
    index('bid_items_vendor_id_idx').on(table.vendorId),
    // Plain FK indexes the delete cascade needs (letting/owner). The owner_item
    // indexes are partial (WHERE deleted_at IS NULL), so a cascade that matches
    // every row can't use them — without these, deleting an owner seq-scans ~10M
    // rows to a timeout.
    index('bid_items_letting_id_idx').on(table.lettingId),
    index('bid_items_owner_id_idx').on(table.ownerId),
    index('bid_items_organization_id_idx')
      .on(table.organizationId)
      .where(sql`organization_id IS NOT NULL`),
    index('bid_items_owner_item_normalized_history_idx')
      .using(
        'btree',
        table.ownerId,
        table.itemNumberNormalized,
        sql`${table.lettingDate} DESC NULLS LAST`,
        sql`${table.id} DESC`,
      )
      .where(sql`deleted_at IS NULL AND item_number_normalized IS NOT NULL`),
  ],
);

export const payEstimates = pgTable(
  'pay_estimates',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    // Owner is the sync key (warehouse fetches per owner_uid) and RLS scopes
    // through it, so it's NOT NULL — unlike the FKs below. The upsert INNER
    // JOINs owners, so an unresolved owner drops the row rather than inserting
    // a null that would bypass tenant isolation / owner-scoped cleanup.
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, {
        onDelete: 'cascade',
      }),
    // FKs into the shared canonical model. Nullable by design: a pay estimate
    // for a not-yet-ingested contract is still valid data — keep the row, keep
    // the raw contract_id so it stays usable without the resolved FK.
    proposalId: uuid('proposal_id').references(() => proposals.id, {
      onDelete: 'set null',
    }),
    vendorId: uuid('vendor_id').references(() => vendors.id, {
      onDelete: 'set null',
    }),
    globalVendorId: uuid('global_vendor_id').references(
      () => globalVendors.id,
      { onDelete: 'set null' },
    ),
    stateId: uuid('state_id').references(() => states.id, {
      onDelete: 'set null',
    }),
    hasBidTabContract: boolean('has_bid_tab_contract').default(false),
    contractId: varchar('contract_id', { length: 255 }),
    state: varchar('state', { length: 10 }),
    estimateNumber: integer('estimate_number'),
    estimateType: varchar('estimate_type', { length: 64 }),
    contractorName: varchar('contractor_name', { length: 512 }),
    periodStart: timestamp('period_start'),
    periodEnd: timestamp('period_end'),
    reportGeneratedDate: timestamp('report_generated_date'),
    lettingDate: timestamp('letting_date'),
    awardedDate: timestamp('awarded_date'),
    ntpDate: timestamp('ntp_date'),
    originalCompletionDate: timestamp('original_completion_date'),
    currentCompletionDate: timestamp('current_completion_date'),
    actualCompletionDate: timestamp('actual_completion_date'),
    originalContractAmount: numeric('original_contract_amount', {
      precision: 20,
      scale: 4,
    }),
    netChangeOrderAmount: numeric('net_change_order_amount', {
      precision: 20,
      scale: 4,
    }),
    currentContractAmount: numeric('current_contract_amount', {
      precision: 20,
      scale: 4,
    }),
    pctComplete: numeric('pct_complete', { precision: 10, scale: 4 }),
    thisEstimatePostedItems: numeric('this_estimate_posted_items', {
      precision: 20,
      scale: 4,
    }),
    thisEstimateAdjustments: numeric('this_estimate_adjustments', {
      precision: 20,
      scale: 4,
    }),
    thisEstimatePayable: numeric('this_estimate_payable', {
      precision: 20,
      scale: 4,
    }),
    previousTotal: numeric('previous_total', { precision: 20, scale: 4 }),
    toDateTotal: numeric('to_date_total', { precision: 20, scale: 4 }),
    incentive: numeric('incentive', { precision: 20, scale: 4 }),
    disincentive: numeric('disincentive', { precision: 20, scale: 4 }),
    liquidatedDamage: numeric('liquidated_damage', {
      precision: 20,
      scale: 4,
    }),
    otherContractAdjustments: numeric('other_contract_adjustments', {
      precision: 20,
      scale: 4,
    }),
    otherContractAdjustmentsToDate: numeric(
      'other_contract_adjustments_to_date',
      { precision: 20, scale: 4 },
    ),
    // Stored materials as $ (MoDOT has no stored-vs-installed qty split): PERIOD
    // = this estimate, TO DATE = cumulative balance. From the "Stored Materials
    // Tracking" contract-adjustment line.
    storedMaterialsThisEstimate: numeric('stored_materials_this_estimate', {
      precision: 20,
      scale: 4,
    }),
    storedMaterialsToDate: numeric('stored_materials_to_date', {
      precision: 20,
      scale: 4,
    }),
    sourceFile: varchar('source_file', { length: 512 }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('pay_estimates_external_id_unique').on(table.externalId),
    index('pay_estimates_owner_id_idx').on(table.ownerId),
    index('pay_estimates_proposal_id_idx').on(table.proposalId),
    index('pay_estimates_vendor_id_idx').on(table.vendorId),
    index('pay_estimates_global_vendor_id_idx').on(table.globalVendorId),
    index('pay_estimates_contract_id_idx').on(table.contractId),
    index('pay_estimates_natural_key_idx').on(
      table.ownerId,
      table.contractId,
      table.state,
      table.estimateNumber,
    ),
    index('pay_estimates_deleted_at_idx')
      .on(table.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

export const payEstimateItems = pgTable(
  'pay_estimate_items',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    payEstimateId: uuid('pay_estimate_id').references(() => payEstimates.id, {
      onDelete: 'cascade',
    }),
    // Owner is the sync key + RLS scope → NOT NULL (upsert INNER JOINs owners).
    ownerId: uuid('owner_id')
      .notNull()
      .references(() => owners.id, {
        onDelete: 'cascade',
      }),
    // Nullable FKs into the shared canonical model — misc / change-order /
    // stockpile lines have no bid-tab counterpart. Keep the row, keep item_code.
    proposalId: uuid('proposal_id').references(() => proposals.id, {
      onDelete: 'set null',
    }),
    itemId: uuid('item_id').references(() => items.id, {
      onDelete: 'set null',
    }),
    unitId: uuid('unit_id').references(() => units.id, {
      onDelete: 'set null',
    }),
    stableKey: varchar('stable_key', { length: 255 }),
    state: varchar('state', { length: 10 }),
    contractId: varchar('contract_id', { length: 255 }),
    estimateNumber: integer('estimate_number'),
    projectNumber: varchar('project_number', { length: 255 }),
    category: varchar('category', { length: 255 }),
    lineNumber: varchar('line_number', { length: 64 }),
    itemCode: varchar('item_code', { length: 255 }),
    itemDescription: text('item_description'),
    ownerItemDescription: text('owner_item_description'),
    numberNormalized: text('number_normalized'),
    categoryCode: varchar('category_code', { length: 64 }),
    categoryName: varchar('category_name', { length: 255 }),
    isTrueLumpSum: boolean('is_true_lump_sum').default(false),
    rawUnit: varchar('raw_unit', { length: 64 }),
    normalizedUnit: varchar('normalized_unit', { length: 64 }),
    unitPrice: numeric('unit_price', { precision: 20, scale: 10 }),
    bidQuantity: numeric('bid_quantity', { precision: 20, scale: 10 }),
    netChangeOrderQty: numeric('net_change_order_qty', {
      precision: 20,
      scale: 10,
    }),
    totalCurrentQuantity: numeric('total_current_quantity', {
      precision: 20,
      scale: 10,
    }),
    totalPostedQty: numeric('total_posted_qty', {
      precision: 20,
      scale: 10,
    }),
    // Quantity installed THIS estimate period (the per-period delta of posted
    // qty). From the mart's qty_this_period; reconciles to the source PDF's
    // "Items Paid This Estimate Period" section to the unit. ~7.8% non-zero.
    qtyThisPeriod: numeric('qty_this_period', {
      precision: 20,
      scale: 10,
    }),
    totalValueToDate: numeric('total_value_to_date', {
      precision: 20,
      scale: 4,
    }),
    // Stored materials for this item, this estimate ($ — MoDOT has no stored
    // qty). From the Construction/Stockpile line-item transaction (~35% of
    // contracts). Negative = material moving out of stockpile into installed.
    stockpileThisEstimate: numeric('stockpile_this_estimate', {
      precision: 20,
      scale: 4,
    }),
    isChangeOrder: boolean('is_change_order').default(false),
    sourceFile: varchar('source_file', { length: 512 }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('pay_estimate_items_external_id_unique').on(table.externalId),
    index('pay_estimate_items_pay_estimate_id_idx').on(table.payEstimateId),
    index('pay_estimate_items_owner_id_idx').on(table.ownerId),
    index('pay_estimate_items_proposal_id_idx').on(table.proposalId),
    index('pay_estimate_items_item_id_idx').on(table.itemId),
    index('pay_estimate_items_number_normalized_idx')
      .on(table.ownerId, table.numberNormalized)
      .where(sql`deleted_at IS NULL AND number_normalized IS NOT NULL`),
    // Supports the over/under-run report's DISTINCT ON (proposal_id, item_id)
    // ORDER BY estimate_number DESC — without it that read sorts all ~880k rows.
    index('pay_estimate_items_proposal_item_estimate_idx')
      .on(table.proposalId, table.itemId, table.estimateNumber.desc())
      .where(sql`deleted_at IS NULL AND item_id IS NOT NULL`),
    // Estimate-detail dialog (getRollupByEstimate) and the per-proposal rollup
    // filter on (proposal_id, estimate_number). Without this, both match every
    // estimate period for the contract by proposal_id alone, then heap-fetch and
    // discard ~90% by estimate_number — 530ms cold on a large contract.
    index('pay_estimate_items_proposal_estimate_idx')
      .on(table.proposalId, table.estimateNumber)
      .where(sql`deleted_at IS NULL`),
    // Backlog "tons remaining" (leaderboard + by-year + active-projects) joins
    // pay_estimate_items per estimate filtered to normalized_unit='TON'. The
    // partial predicate limits the index to TON rows, and carrying the two
    // quantities in the key makes the join an index scan with no heap fetch —
    // the tons subquery was ~200ms cold (23k heap blocks) without it.
    index('pay_estimate_items_ton_qty_idx')
      .on(table.payEstimateId, table.totalCurrentQuantity, table.totalPostedQty)
      .where(sql`deleted_at IS NULL AND normalized_unit = 'TON'`),
    // Vendor Active Throughput volume (getThroughput* per estimate) sums
    // qty_this_period for TON/CY. Without this the vol CTE parallel-seq-scans all
    // ~1.2M items to keep the TON/CY rows — ~800ms on the per-vendor detail. The
    // partial predicate + qty in the key make it a per-estimate index scan.
    // owner_id is carried in the key so the tenant RLS predicate
    // (owner_id IN organization_owners) is satisfied FROM THE INDEX — otherwise
    // RLS knocks the throughput-leaderboard tons join off the index-only scan
    // onto a bitmap heap scan (~10s cold). With it: index-only, ~160ms (EDG-2145).
    index('pay_estimate_items_ton_cy_period_idx')
      .on(
        table.payEstimateId,
        table.normalizedUnit,
        table.qtyThisPeriod,
        table.ownerId,
      )
      .where(sql`deleted_at IS NULL AND normalized_unit IN ('TON','CY')`),
    index('pay_estimate_items_deleted_at_idx')
      .on(table.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

/**
 * Bounding box of an extracted region on a source PDF page.
 * Normalized PDF coordinates (origin bottom-left) as [x0, y0, x1, y1].
 * Stored as jsonb so the shape can evolve without a migration.
 */
export type SourceBoundingBox = [number, number, number, number];

/** A validation problem found on a single extracted field. */
export interface ExtractedFieldError {
  code: string;
  message?: string;
  severity?: 'error' | 'warning' | 'info';
  /** ISO timestamp when this error was resolved during review, if any. */
  resolvedAt?: string;
}

/** Source-trace + validation for one extracted field (a value in a record). */
export interface ExtractedFieldAnnotation {
  page?: number;
  bbox?: SourceBoundingBox;
  errors?: ExtractedFieldError[];
}

/**
 * Per-record annotations, keyed by extracted field name (e.g. 'unit_price').
 * Each field carries where it was read from (page/bbox) and any validation
 * errors, so the review surface can hover a value, jump to its page, and flag
 * problems — all from one row. A reserved '*' key can hold record-level notes.
 */
export type ExtractedRecordAnnotations = Record<
  string,
  ExtractedFieldAnnotation
>;

/**
 * Source files backing a custom (tenant-uploaded) bid tab. One-to-many:
 * a single custom bid tab (proposal under a custom owner) can be assembled
 * from multiple uploaded PDFs. Document-level source linkage lives here;
 * per-record source-trace lives in {@link customBidTabSources}.
 */
export const customBidTabFiles = pgTable(
  'custom_bid_tab_files',
  {
    ...id,
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'cascade' }),
    fileId: uuid('file_id')
      .notNull()
      .references(() => files.id, { onDelete: 'cascade' }),
    // Upload/review session grouping these files (see EDG-1176 upload surface).
    uploadSessionId: uuid('upload_session_id'),
    metadata: jsonb('metadata')
      .$type<Record<string, unknown>>()
      .default(sql`'{}'::jsonb`),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('custom_bid_tab_files_proposal_id_file_id_unique').on(
      table.proposalId,
      table.fileId,
    ),
    index('custom_bid_tab_files_organization_id_idx').on(table.organizationId),
    index('custom_bid_tab_files_proposal_id_idx').on(table.proposalId),
    index('custom_bid_tab_files_file_id_idx').on(table.fileId),
    index('custom_bid_tab_files_upload_session_id_idx').on(
      table.uploadSessionId,
    ),
  ],
);

/**
 * Polymorphic per-record source-trace for custom bid tabs (EDG-1178). For any
 * extracted record — a letting, proposal, proposal item, bid, bid item, vendor
 * — this maps it back to the source file + page (+ optional bbox) so the review
 * surface can jump from an output value to where it was read in the PDF. PK is
 * (entity_type, entity_id): one row per extracted record, an index-only lookup,
 * and the hot DOT tables stay untouched (DOT rows have no row here). entity_id
 * is intentionally not a hard FK (it points at different tables by entity_type);
 * integrity is enforced by the writing repository.
 *
 * `annotations` is keyed by field name — per-field page/bbox + validation
 * errors live together, so review-time errors are folded in here rather than a
 * separate table.
 */
export const customBidTabSources = pgTable(
  'custom_bid_tab_sources',
  {
    entityType: varchar('entity_type', { length: 64 }).notNull(),
    entityId: uuid('entity_id').notNull(),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    fileId: uuid('file_id')
      .notNull()
      .references(() => files.id, { onDelete: 'cascade' }),
    annotations: jsonb('annotations')
      .$type<ExtractedRecordAnnotations>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.entityType, table.entityId] }),
    index('custom_bid_tab_sources_organization_id_idx').on(
      table.organizationId,
    ),
    index('custom_bid_tab_sources_entity_id_idx').on(table.entityId),
    index('custom_bid_tab_sources_file_id_idx').on(table.fileId),
  ],
);

/**
 * A user-created folder grouping this org's custom proposals (EDG-1911).
 * Org-shared and flat — no nesting and no per-user ownership. A custom proposal
 * sits in at most one folder through `custom_proposals.folder_id`; NULL there is
 * the "unfiled" bucket. Deleting a folder unfiles its custom proposals and never
 * deletes them.
 */
export const customProposalFolders = pgTable(
  'custom_proposal_folders',
  {
    ...id,
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    name: varchar('name', { length: 255 }).notNull(),
    description: text('description'),
    /** The folder this one sits in. Null is the top of the list. */
    parentId: uuid('parent_id'),
    // Manual display order. Folders are user-arranged, so the list cannot fall
    // back to name; ties break on name only to keep the order total.
    position: integer('position').notNull().default(0),
    ...timestamps,
  },
  (table) => [
    index('custom_proposal_folders_organization_id_idx').on(
      table.organizationId,
    ),
    index('custom_proposal_folders_organization_id_position_idx').on(
      table.organizationId,
      table.position,
    ),
    index('custom_proposal_folders_organization_id_parent_id_idx').on(
      table.organizationId,
      table.parentId,
    ),
    // Custom proposals pin this folder's org via this composite target.
    unique('custom_proposal_folders_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
    // Composite self-FK pinning the parent's org to this row's org. NO ACTION
    // for the same reason the bid tabs' folder FK is: a delete takes the whole
    // subtree out in one statement, after moving what it held up a level.
    foreignKey({
      columns: [table.parentId, table.organizationId],
      foreignColumns: [table.id, table.organizationId],
      name: 'custom_proposal_folders_parent_id_organization_id_fk',
    }),
  ],
);

/**
 * A custom proposal is the top-level bid-first record.
 * create-FIRST unit: a tenant builds it, then uploads files INTO it to enrich.
 * It is the custom-origin counterpart of a `proposals` row but is its OWN table
 * — never a `proposals` row. Strong identity keys (`owner_id`, `contract_id`)
 * are nullable columns OUTSIDE the jsonb (empty at first). `staging` holds the
 * published/working matrix (read values + inline resolved-link fields). On the
 * first publish it materializes into the platform `proposals`/`bids`/`bid_items`
 * and stamps `published_proposal_id`.
 */
export const customProposals = pgTable(
  'custom_proposals',
  {
    ...id,
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    // Strong identity — nullable, empty at creation, filled by enrichment later.
    ownerId: uuid('owner_id').references(() => owners.id, {
      onDelete: 'set null',
    }),
    // The folder this custom proposal is filed in; NULL is "unfiled". Org-pinned
    // by the composite FK below, so it can never point at another org's folder.
    folderId: uuid('folder_id'),
    contractId: varchar('contract_id', { length: 255 }),
    title: text('title'),
    description: text('description'),
    // Which extraction feeds `staging` (pick-one in v1; merge is deferred).
    primaryExtractionId: uuid('primary_extraction_id'),
    // The platform proposal created by publishing this custom proposal.
    publishedProposalId: uuid('published_proposal_id').references(
      () => proposals.id,
      { onDelete: 'set null' },
    ),
    reviewStatus: extractionReviewStatusEnum('review_status')
      .notNull()
      .default('pending'),
    // Defaults to `normalizing`, NOT `normalized`: a fresh bid is NOT done until
    // the pipeline explicitly finishes and sets `normalized`. Defaulting to
    // `normalized` was a lie — it made an untouched bid read as "ready" in the
    // window between extraction markReady and normalization starting, so the UI
    // let the user onto an empty Details before the grid was populated.
    normalizationStatus: customProposalNormalizationStatusEnum(
      'normalization_status',
    )
      .notNull()
      .default('normalizing'),
    reviewedAt: timestamp('reviewed_at', { mode: 'date' }),
    reviewedBy: uuid('reviewed_by').references(() => users.id, {
      onDelete: 'set null',
    }),
    // The published/working matrix — read values + inline null-start resolved
    // links (see CustomProposalStaging in domains). NULL until first pick/edit.
    staging: jsonb('staging').$type<Record<string, unknown>>(),
    // The DURABLE normalization store — resolved decisions (per-line catalog
    // match / category / collections / liquid-AC / unit, per-vendor link), kept
    // SEPARATE from `staging` so a "reset to extraction" never loses the paid
    // normalization; a reset re-applies it onto the fresh grid. NULL until first
    // normalize. See normalize/normalized-store.ts.
    normalized: jsonb('normalized').$type<Record<string, unknown>>(),
    metadata: jsonb('metadata')
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    index('custom_proposals_organization_id_idx').on(table.organizationId),
    index('custom_proposals_organization_id_created_at_idx').on(
      table.organizationId,
      table.createdAt.desc(),
    ),
    index('custom_proposals_owner_id_idx').on(table.ownerId),
    index('custom_proposals_folder_id_idx').on(table.folderId),
    index('custom_proposals_published_proposal_id_idx').on(
      table.publishedProposalId,
    ),
    index('custom_proposals_review_status_idx')
      .on(table.reviewStatus)
      .where(sql`review_status = 'pending'`),
    // Children pin the parent's org via this composite target.
    unique('custom_proposals_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
    // Composite FK pinning the folder's org to this row's org. Deliberately NO
    // ACTION: `ON DELETE SET NULL` would also null `organization_id`, which is
    // NOT NULL. Folder deletes unfile their rows first, in the same transaction.
    foreignKey({
      columns: [table.folderId, table.organizationId],
      foreignColumns: [
        customProposalFolders.id,
        customProposalFolders.organizationId,
      ],
      name: 'custom_proposals_folder_id_organization_id_fk',
    }),
  ],
);

/**
 * Lifecycle of one engine run over an uploaded bid-tab file. This is the
 * bid-tab extraction subsystem's OWN state — it is intentionally NOT folded
 * into the shared `files.status` / `upload_sessions.status` enums (those stay
 * generic to every upload type). The upload transport ends at completion; this
 * begins there.
 */
export const bidTabExtractionStatusEnum = pgEnum('bid_tab_extraction_status', [
  'queued',
  'running',
  'ready',
  'failed',
  // Terminal no-signal verdicts and legacy capability waits. Backfill resumes
  // known legacy capability sessions on their original extraction row.
  'held',
]);

export const customBidTabExtractionCostStatusEnum = pgEnum(
  'custom_bid_tab_extraction_cost_status',
  ['pending', 'completed', 'failed', 'unknown'],
);

/**
 * Intermediate staging for a bid-tab extraction (EDG-1183). One row per
 * (file, run): the engine's output lands here for human review BEFORE anything
 * touches the production bid-tab tables (proposals/bids/items). `reviewStatus`
 * gates that promotion — pending until a human approves.
 *
 * TWO jsonb payloads, per the Core-2 store split:
 *  - `consolidated` — the STABLE product (the engine's consolidated.json). What the
 *    platform renders / ranks / ships. Its shape is the durable contract.
 *  - `artifacts` — the version-stamped DEBUG bag: every intermediate stage output
 *    (route.json / blueprint.json / plan.json / …) keyed by name, each wrapped in a
 *    k8s-style `{ apiVersion, kind, data }` envelope. Shape CHANGES per engine
 *    version; the platform never renders it — it's for debugging / re-runs only.
 * `validation`/`metadata` stay loose (engine-owned shapes; the db avoids coupling).
 */
export const customProposalExtractions = pgTable(
  'custom_proposal_extractions',
  {
    ...id,
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    // The parent custom proposal. NOT NULL: every extraction belongs to one.
    customProposalId: uuid('custom_proposal_id').notNull(),
    fileId: uuid('file_id').notNull(),
    // Weak batch grouping — combines files dropped in one shot. Replaces the
    // strong upload_session link.
    batchId: uuid('batch_id'),
    runLabel: varchar('run_label', { length: 32 }),
    status: bidTabExtractionStatusEnum('status').notNull().default('queued'),
    // The document-shape bucket (BidTabSegment), stamped after CLASSIFY DOCUMENT
    // and refined after BLUEPRINT. NULL until classified.
    segment: varchar('segment', { length: 40 }),
    shippable: boolean('shippable'),
    orgCostCapBypass: boolean('org_cost_cap_bypass').notNull().default(false),
    // The STABLE product — the engine's consolidated.json. WRITE-ONCE by the
    // engine. Per-file raw extraction; the custom proposal's picked/merged product
    // lives on custom_proposals.staging.
    consolidated: jsonb('consolidated')
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    // The version-stamped DEBUG bag — every intermediate artifact by key (opaque to
    // the platform; changes per engine version).
    artifacts: jsonb('artifacts')
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    validation: jsonb('validation')
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    metadata: jsonb('metadata')
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    error: text('error'),
    // WHY it failed, from the user-facing taxonomy (the taxonomy itself is
    // EXTRACTION_FAILURE_REASONS in @edgevanta/domains, not a PG enum, so it
    // grows without a migration). NULL on a failure no classifier recognized
    // (the UI keeps its generic copy).
    failureReason: varchar('failure_reason', { length: 40 }),
    ...timestamps,
  },
  (table) => [
    index('custom_proposal_extractions_organization_id_idx').on(
      table.organizationId,
    ),
    index('custom_proposal_extractions_custom_proposal_id_idx').on(
      table.customProposalId,
    ),
    // The list runs two correlated `order by created_at desc limit 1` subqueries
    // per row (latest status, latest failure reason) and Postgres cannot share
    // the subplans — they project different columns. Descending on created_at so
    // each one is an index scan with no sort.
    index('custom_proposal_extractions_custom_proposal_id_created_at_idx').on(
      table.customProposalId,
      table.createdAt.desc(),
    ),
    index('custom_proposal_extractions_file_id_idx').on(table.fileId),
    index('custom_proposal_extractions_batch_id_idx').on(table.batchId),
    // Composite FK pinning the parent file's org to this row's org.
    foreignKey({
      columns: [table.fileId, table.organizationId],
      foreignColumns: [files.id, files.organizationId],
      name: 'custom_proposal_extractions_file_id_organization_id_fk',
    }).onDelete('cascade'),
    // Composite FK pinning the parent custom proposal's org to this row's org.
    foreignKey({
      columns: [table.customProposalId, table.organizationId],
      foreignColumns: [customProposals.id, customProposals.organizationId],
      name: 'custom_proposal_extractions_custom_proposal_id_organization_id_fk',
    }).onDelete('cascade'),
    unique('custom_proposal_extractions_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
  ],
);

/**
 * Prospective cost ledger for Custom Bid Tabs extraction model attempts. One
 * row is reserved when an attempt starts, fixing `occurredAt` for UTC month
 * attribution even when completion or a retry crosses a month boundary. The
 * extraction reference becomes null on deletion so the ledger remains durable.
 */
export const customBidTabExtractionCosts = pgTable(
  'custom_bid_tab_extraction_costs',
  {
    ...id,
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    extractionId: uuid('extraction_id'),
    extractionOrganizationId: uuid('extraction_organization_id'),
    idempotencyKey: varchar('idempotency_key', { length: 255 }).notNull(),
    provider: varchar('provider', { length: 64 }).notNull(),
    model: varchar('model', { length: 128 }).notNull(),
    stage: varchar('stage', { length: 64 }).notNull(),
    attempt: integer('attempt').notNull(),
    orgCostCapBypass: boolean('org_cost_cap_bypass'),
    status: customBidTabExtractionCostStatusEnum('status')
      .notNull()
      .default('pending'),
    inputTokens: integer('input_tokens').notNull().default(0),
    outputTokens: integer('output_tokens').notNull().default(0),
    cacheWriteTokens: integer('cache_write_tokens').notNull().default(0),
    cacheReadTokens: integer('cache_read_tokens').notNull().default(0),
    costUsd: numeric('cost_usd', { precision: 20, scale: 10 }),
    occurredAt: timestamp('occurred_at', { mode: 'date' })
      .notNull()
      .defaultNow(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('custom_bid_tab_extraction_costs_idempotency_key_unique').on(
      table.idempotencyKey,
    ),
    index('custom_bid_tab_extraction_costs_extraction_id_idx').on(
      table.extractionId,
    ),
    index('custom_bid_tab_extraction_costs_organization_id_occurred_at_idx').on(
      table.organizationId,
      table.occurredAt,
    ),
    foreignKey({
      columns: [table.extractionId, table.extractionOrganizationId],
      foreignColumns: [
        customProposalExtractions.id,
        customProposalExtractions.organizationId,
      ],
      name: 'custom_bid_tab_extraction_costs_extraction_organization_fk',
    }).onDelete('set null'),
    check('custom_bid_tab_extraction_costs_attempt_positive', sql`attempt > 0`),
    check(
      'custom_bid_tab_extraction_costs_tokens_nonnegative',
      sql`input_tokens >= 0 AND output_tokens >= 0 AND cache_write_tokens >= 0 AND cache_read_tokens >= 0`,
    ),
    check(
      'custom_bid_tab_extraction_costs_cost_usd_nonnegative',
      sql`cost_usd IS NULL OR cost_usd >= 0`,
    ),
    check(
      'custom_bid_tab_extraction_costs_extraction_organization_consistent',
      sql`(extraction_id IS NULL AND extraction_organization_id IS NULL) OR (extraction_id IS NOT NULL AND extraction_organization_id IS NOT NULL AND extraction_organization_id = organization_id)`,
    ),
    pgPolicy('tenant_org_isolation', {
      for: 'all',
      to: 'tenant',
      using: sql`organization_id = nullif(current_setting('app.current_organization_id', true), '')::uuid`,
      withCheck: sql`organization_id = nullif(current_setting('app.current_organization_id', true), '')::uuid`,
    }),
  ],
).enableRLS();

/**
 * Per-page state for one extraction. One row per page lets the later per-page
 * fan-out write each page independently; for the v1 whole-doc job the receiver
 * writes them in a single batch. `extraction` mirrors the engine's per-page
 * output (loose shape).
 */
export const customProposalExtractionPages = pgTable(
  'custom_proposal_extraction_pages',
  {
    ...id,
    extractionId: uuid('extraction_id')
      .notNull()
      .references(() => customProposalExtractions.id, {
        onDelete: 'cascade',
      }),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    pageNumber: integer('page_number').notNull(),
    label: varchar('label', { length: 64 }),
    extraction: jsonb('extraction').$type<Record<string, unknown>>(),
    imageRef: varchar('image_ref', { length: 1024 }),
    error: text('error'),
    ...timestamps,
  },
  (table) => [
    uniqueIndex(
      'custom_proposal_extraction_pages_extraction_id_page_number_uniq',
    ).on(table.extractionId, table.pageNumber),
    index('custom_proposal_extraction_pages_extraction_id_idx').on(
      table.extractionId,
    ),
    index('custom_proposal_extraction_pages_organization_id_idx').on(
      table.organizationId,
    ),
  ],
);

export const tokenUsageOperationEnum = pgEnum(
  'token_usage_operations',
  TOKEN_USAGE_OPERATIONS,
);

export const tokenUsages = pgTable(
  'token_usages',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, {
        onDelete: 'restrict',
      })
      .notNull(),
    userId: uuid('user_id')
      .references(() => users.id, {
        onDelete: 'restrict',
      })
      .notNull(),
    provider: varchar('provider').notNull(),
    model: varchar('model').notNull(),
    operation: tokenUsageOperationEnum('operation').notNull(),
    inputTokens: integer('input_tokens').notNull(),
    outputTokens: integer('output_tokens').notNull(),
    totalTokens: integer('total_tokens').notNull(),
    inputCost: decimal('input_cost', { precision: 10, scale: 6 }).notNull(),
    outputCost: decimal('output_cost', { precision: 10, scale: 6 }).notNull(),
    totalCost: decimal('total_cost', { precision: 10, scale: 6 }).notNull(),
    durationInMs: integer('duration_in_ms').notNull(),
    ...timestamps,
  },
  (t) => [
    index('token_usage_organization_id_idx').on(t.organizationId),
    index('token_usage_user_id_idx').on(t.userId),
    index('token_usage_provider_idx').on(t.provider),
    index('token_usage_model_idx').on(t.model),
    index('token_usage_operation_idx').on(t.operation),
    index('token_usage_created_at_idx').on(t.createdAt),
  ],
);

export const fileEmbeddings = pgTable(
  'file_embeddings',
  {
    ...id,
    fileId: uuid('file_id')
      .notNull()
      .references(() => files.id, { onDelete: 'cascade' }),
    pageStart: integer('page_start').notNull(),
    pageEnd: integer('page_end').notNull(),
    section: text('section'),
    position: integer('position'),
    text: text('text').notNull(),
    bbox: jsonb('bbox'),
    model: text('model').notNull(),
    dim: integer('dim').notNull(),
    embedding: vector({ length: 3072 }),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    ...timestamps,
  },
  (table) => [
    index('file_embeddings_file_id_idx').on(table.fileId),
    index('file_embeddings_organization_id_idx').on(table.organizationId),
    index('file_embeddings_file_id_covering_idx').on(
      table.fileId,
      table.id,
      table.pageStart,
      table.pageEnd,
      table.createdAt,
    ),
    index('file_embeddings_file_org_page_idx').on(
      table.fileId,
      table.organizationId,
      table.pageStart,
      table.pageEnd,
    ),
    index('file_embeddings_file_position_idx').on(table.fileId, table.position),
  ],
);

export const estimators = pgTable(
  'estimators',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('estimators_org_name_unique').on(
      table.organizationId,
      table.name,
    ),
    index('estimators_org_idx').on(table.organizationId),
  ],
);

export const businessUnits = pgTable(
  'business_units',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('business_units_org_name_unique').on(
      table.organizationId,
      table.name,
    ),
    index('business_units_org_idx').on(table.organizationId),
  ],
);

export const workTypes = pgTable(
  'work_types',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('work_types_org_name_unique').on(
      table.organizationId,
      table.name,
    ),
    index('work_types_org_idx').on(table.organizationId),
  ],
);

export const forecasts = pgTable(
  'forecasts',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    userId: uuid('user_id')
      .references(() => users.id, { onDelete: 'restrict' })
      .notNull(),
    date: timestamp('date'),
    contractId: varchar('contract_id', { length: 255 }),
    ownerId: uuid('owner_id').references(() => owners.id, {
      onDelete: 'restrict',
    }),
    ownerExternalId: varchar('owner_external_id', { length: 255 }),
    owner: varchar('owner', { length: 255 }),
    counties: text('counties'),
    stateId: uuid('state_id').references(() => states.id, {
      onDelete: 'set null',
    }),
    route: varchar('route', { length: 255 }),
    description: text('description'),
    scopeNotes: text('scope_notes'),
    operationalEfficiency: text('operational_efficiency'),
    riskAndContingency: text('risk_and_contingency'),
    backlog: text('backlog'),
    landscapeSeededAt: timestamp('landscape_seeded_at'),
    // Soft-delete. deleteForecast UPDATEs this rather than DELETEing
    // the row so accidental MCP / chat invocations are recoverable.
    // Every read path must filter `deleted_at IS NULL`.
    deletedAt: timestamp('deleted_at', { mode: 'date' }),
    ...timestamps,
  },
  (table) => [
    index('forecasts_organization_id_idx').on(table.organizationId),
    index('forecasts_user_id_idx').on(table.userId),
    index('forecasts_date_idx').on(table.date),
    index('forecasts_owner_id_idx').on(table.ownerId),
    index('forecasts_owner_external_id_idx').on(table.ownerExternalId),
    // Composite index supports the hot list-query pattern
    // (orgId AND deleted_at IS NULL) without a full sequential scan
    // once forecasts.deleted_at is populated at scale.
    index('forecasts_org_deleted_idx').on(
      table.organizationId,
      table.deletedAt,
    ),
    // FK target for the composite (forecast_id, organization_id) FKs on every
    // forecast child table (EDG-1180): pins each child's org to its parent
    // forecast's org at the DB layer, so a write can't tag a child orgA while
    // pointing at orgB's forecast. `id` is already the PK; this composite UNIQUE
    // is what those composite FKs reference.
    unique('forecasts_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
  ],
);

export const forecastCounties = pgTable(
  'forecast_counties',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`.
    forecastId: uuid('forecast_id').notNull(),
    countyId: uuid('county_id')
      .notNull()
      .references(() => counties.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    ...timestamps,
  },
  (table) => [
    index('forecast_counties_forecast_id_idx').on(table.forecastId),
    index('forecast_counties_county_id_idx').on(table.countyId),
    index('forecast_counties_organization_id_idx').on(table.organizationId),
    uniqueIndex('forecast_counties_forecast_id_county_id_unique').on(
      table.forecastId,
      table.countyId,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_counties_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastDistricts = pgTable(
  'forecast_districts',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`.
    forecastId: uuid('forecast_id').notNull(),
    districtId: uuid('district_id')
      .notNull()
      .references(() => districts.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    ...timestamps,
  },
  (table) => [
    index('forecast_districts_forecast_id_idx').on(table.forecastId),
    index('forecast_districts_district_id_idx').on(table.districtId),
    index('forecast_districts_organization_id_idx').on(table.organizationId),
    uniqueIndex('forecast_districts_forecast_id_district_id_unique').on(
      table.forecastId,
      table.districtId,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_districts_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

/** @deprecated TODO [EV3-1138]: Remove after migration — use proposal_extension_estimators instead */
export const forecastsToEstimators = pgTable(
  'forecasts_to_estimators',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`. The estimator_id FK is
    // out of scope (not a forecast_id -> forecasts link).
    forecastId: uuid('forecast_id').notNull(),
    estimatorId: uuid('estimator_id')
      .notNull()
      .references(() => estimators.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    ...timestamps,
  },
  (table) => [
    index('forecasts_to_estimators_forecast_id_idx').on(table.forecastId),
    index('forecasts_to_estimators_estimator_id_idx').on(table.estimatorId),
    index('forecasts_to_estimators_organization_id_idx').on(
      table.organizationId,
    ),
    uniqueIndex('forecasts_to_estimators_forecast_id_estimator_id_unique').on(
      table.forecastId,
      table.estimatorId,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecasts_to_estimators_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastItems = pgTable(
  'forecast_items',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`.
    forecastId: uuid('forecast_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    type: varchar('type', { length: 20 }).notNull(),
    lineNumber: integer('line_number').notNull(),
    itemNumber: varchar('item_number', { length: 255 }).notNull(),
    description: text('description'),
    quantity: numeric('quantity', { precision: 20, scale: 10 }).notNull(),
    unit: varchar('unit', { length: 50 }),
    unitId: uuid('unit_id').references(() => units.id, {
      onDelete: 'set null',
    }),
    unitPrice: numeric('unit_price', { precision: 20, scale: 10 }),
    extension: numeric('extension', { precision: 20, scale: 10 }),
    isPriceLinked: boolean('is_price_linked').default(false).notNull(),
    categoryId: uuid('category_id').references(() => itemCategories.id, {
      onDelete: 'set null',
    }),
    ...timestamps,
  },
  (table) => [
    index('forecast_items_forecast_id_idx').on(table.forecastId),
    index('forecast_items_organization_id_idx').on(table.organizationId),
    index('forecast_items_forecast_id_type_idx').on(
      table.forecastId,
      table.type,
    ),
    // FK target for the composite (forecast_item_id, organization_id) FK on
    // forecast_results (EDG-1180): pins the result's org to its parent item's
    // org at the DB layer, so a write can't tag a result orgA while pointing at
    // orgB's item. `id` is already the PK; this composite UNIQUE is what the
    // composite FK references.
    unique('forecast_items_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_items_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastResults = pgTable(
  'forecast_results',
  {
    ...id,
    // Composite FKs to forecast_configs / forecast_items (pinning parent org ==
    // row org) are declared in the table-config callback below, so these columns
    // intentionally carry no inline single-column `.references()`.
    forecastConfigId: uuid('forecast_config_id').notNull(),
    forecastItemId: uuid('forecast_item_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    type: varchar('type', { length: 20 }).notNull(),
    unitPrice: numeric('unit_price', { precision: 20, scale: 10 }),
    unitPriceOverride: numeric('unit_price_override', {
      precision: 20,
      scale: 10,
    }),
    // A lump-sum line has no stored price on the landscape: the column
    // rebuilds it as final_total * ratio / quantity. At 8 decimals one unit
    // in the last place is 18.6c on an $18.6M forecast, so a line rounded to
    // $920,000 rendered $919,999.93 and the column stopped footing to the
    // total (EDG-1893). 18 decimals puts that error below 1e-11 dollars.
    lumpSumRatioOverride: numeric('lump_sum_ratio_override', {
      precision: 20,
      scale: 18,
    }),
    extension: numeric('extension', { precision: 20, scale: 10 }),
    metadata: jsonb('metadata').$type<{
      bidCount?: number;
      totalQuantity?: string;
      lumpSumRatio?: number;
      fallbackLevel?: string;
      isLumpSum?: boolean;
      matchedItemNumber?: string;
      matchedDescription?: string;
      matchedOwnerId?: string;
      similarityScore?: number;
      noDataReason?: string;
    }>(),
    calculatedAt: timestamp('calculated_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('forecast_results_config_item_unique').on(
      table.forecastConfigId,
      table.forecastItemId,
    ),
    index('forecast_results_config_id_idx').on(table.forecastConfigId),
    index('forecast_results_item_id_idx').on(table.forecastItemId),
    index('forecast_results_organization_id_idx').on(table.organizationId),
    // Composite FKs that pin the parent's org to this row's org (EDG-1180).
    // Replaces the simple single-column FKs: the database now rejects any
    // forecast_results write whose organization_id disagrees with its parent
    // config/item org, closing the cross-tenant data-poisoning path at the DB
    // layer (FK constraints are not bypassed by the table-owner role, so this
    // also protects the live app path). ON DELETE cascade is preserved.
    foreignKey({
      columns: [table.forecastConfigId, table.organizationId],
      foreignColumns: [forecastConfigs.id, forecastConfigs.organizationId],
      name: 'forecast_results_config_id_organization_id_fk',
    }).onDelete('cascade'),
    foreignKey({
      columns: [table.forecastItemId, table.organizationId],
      foreignColumns: [forecastItems.id, forecastItems.organizationId],
      name: 'forecast_results_item_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastCompetitors = pgTable(
  'forecast_competitors',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`. The vendor_id FK is out
    // of scope (not a forecast_id -> forecasts link).
    forecastId: uuid('forecast_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    vendorId: uuid('vendor_id')
      .notNull()
      .references(() => vendors.id, {
        onDelete: 'restrict',
      }),
    vendorExternalId: varchar('vendor_external_id', { length: 255 }),
    position: integer('position').notNull(),
    filters: jsonb('filters').$type<{
      counties?: string | null;
      ownerName?: string | null;
      dateFrom?: string | null;
      dateTo?: string | null;
      minBidders?: number | null;
      maxBidders?: number | null;
      minAmount?: number | null;
      maxAmount?: number | null;
      ranks?: string | null;
      bidExternalIds?: string[] | null;
    }>(),
    ...timestamps,
  },
  (table) => [
    index('forecast_competitors_forecast_id_idx').on(table.forecastId),
    index('forecast_competitors_organization_id_idx').on(table.organizationId),
    uniqueIndex('forecast_competitors_forecast_id_position_unique').on(
      table.forecastId,
      table.position,
    ),
    index('forecast_competitors_vendor_external_id_idx').on(
      table.vendorExternalId,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_competitors_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastConfigTargetEnum = pgEnum(
  'forecast_config_target',
  FORECAST_CONFIG_TARGET_VALUES,
);

export const forecastConfigs = pgTable(
  'forecast_configs',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`.
    forecastId: uuid('forecast_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    target: forecastConfigTargetEnum('target').notNull(),
    competitorId: uuid('competitor_id').references(
      () => forecastCompetitors.id,
      {
        onDelete: 'cascade',
      },
    ),
    filters: jsonb('filters').$type<{
      counties?: string | null;
      ownerName?: string | null;
      dateFrom?: string | null;
      dateTo?: string | null;
      minBidders?: number | null;
      maxBidders?: number | null;
      // The job-size filter: OR'd bid-amount windows (EDG-2022). The only
      // bid-amount format — the legacy single `minAmount`/`maxAmount` window
      // was backfilled into it by 0302 and removed by 0303.
      bidSizes?: { min: number; max: number | null }[] | null;
      ranks?: string | null;
      vendorIds?: string[] | null;
      globalVendorIds?: string[] | null;
      vendorName?: string | null;
      bidExternalIds?: string[] | null;
      qtyLowerBound?: number | null;
      qtyUpperBound?: number | null;
      lsBandLower?: number | null;
      lsBandUpper?: number | null;
    }>(),
    position: integer('position').notNull().default(0),
    hidden: boolean('hidden').notNull().default(false),
    calculatedAt: timestamp('calculated_at', { mode: 'date' }),
    ...timestamps,
  },
  (table) => [
    index('forecast_configs_forecast_id_idx').on(table.forecastId),
    index('forecast_configs_organization_id_idx').on(table.organizationId),
    uniqueIndex('forecast_configs_forecast_target_competitor_unique').on(
      table.forecastId,
      table.target,
      table.competitorId,
    ),
    uniqueIndex('forecast_configs_forecast_position_unique').on(
      table.forecastId,
      table.position,
    ),
    // FK target for the composite (forecast_config_id, organization_id) FK on
    // forecast_results (EDG-1180): pins the result's org to its parent config's
    // org at the DB layer, so a write can't tag a result orgA while pointing at
    // orgB's config. `id` is already the PK; this composite UNIQUE is what the
    // composite FK references.
    unique('forecast_configs_id_organization_id_unique').on(
      table.id,
      table.organizationId,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_configs_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastPostBidEntries = pgTable(
  'forecast_post_bid_entries',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`.
    forecastId: uuid('forecast_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    rank: integer('rank'),
    vendorName: varchar('vendor_name', { length: 255 }).notNull(),
    adjustedTotal: numeric('adjusted_total', { precision: 20, scale: 10 }),
    deviation: numeric('deviation', { precision: 20, scale: 10 }),
    ...timestamps,
  },
  (table) => [
    index('forecast_post_bid_entries_forecast_id_idx').on(table.forecastId),
    index('forecast_post_bid_entries_organization_id_idx').on(
      table.organizationId,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_post_bid_entries_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastSimilarBids = pgTable(
  'forecast_similar_bids',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`. The proposal_id FK is out
    // of scope (not a forecast_id -> forecasts link).
    forecastId: uuid('forecast_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'restrict' }),
    rank: integer('rank').notNull(),
    position: integer('position'),
    ...timestamps,
  },
  (table) => [
    index('forecast_similar_bids_forecast_id_idx').on(table.forecastId),
    uniqueIndex('forecast_similar_bids_forecast_proposal_rank_unique').on(
      table.forecastId,
      table.proposalId,
      table.rank,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_similar_bids_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastBidAsReadEntries = pgTable(
  'forecast_bid_as_read_entries',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`.
    forecastId: uuid('forecast_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    rank: integer('rank').notNull(),
    vendorName: varchar('vendor_name', { length: 255 }).notNull(),
    adjustedTotal: numeric('adjusted_total', {
      precision: 20,
      scale: 10,
    }).notNull(),
    deviation: numeric('deviation', { precision: 20, scale: 10 }),
    ...timestamps,
  },
  (table) => [
    index('forecast_bid_as_read_entries_forecast_id_idx').on(table.forecastId),
    index('forecast_bid_as_read_entries_organization_id_idx').on(
      table.organizationId,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_bid_as_read_entries_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const forecastFileTypeEnum = pgEnum('forecast_file_type', [
  FORECAST_FILE_TYPE_VALUES.csvItems,
  FORECAST_FILE_TYPE_VALUES.csvLandscapeItems,
  FORECAST_FILE_TYPE_VALUES.scopeAttachment,
]);

export const forecastFiles = pgTable(
  'forecast_files',
  {
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`. The file_id FK is out of
    // scope (not a forecast_id -> forecasts link).
    forecastId: uuid('forecast_id').notNull(),
    fileId: uuid('file_id')
      .notNull()
      .references(() => files.id, { onDelete: 'cascade' }),
    type: forecastFileTypeEnum('type').notNull(),
    organizationId: uuid('organization_id').notNull(),
    attachedAt: timestamp('attached_at', { withTimezone: true }).defaultNow(),
    attachedBy: uuid('attached_by').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (t) => [
    uniqueIndex('forecast_files_file_id_forecast_id_unique').on(
      t.fileId,
      t.forecastId,
    ),
    index('forecast_files_forecast_id_idx').on(t.forecastId),
    index('forecast_files_forecast_id_type_idx').on(t.forecastId, t.type),
    index('forecast_files_org_id_idx').on(t.organizationId),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [t.forecastId, t.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_files_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const proposalExtensions = pgTable(
  'proposal_extensions',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    ownerId: uuid('owner_id')
      .references(() => owners.id, { onDelete: 'restrict' })
      .notNull(),
    proposalId: uuid('proposal_id').references(() => proposals.id, {
      onDelete: 'restrict',
    }),
    markupPercent: numeric('markup_percent', { precision: 10, scale: 4 }),
    overheadPercent: numeric('overhead_percent', { precision: 10, scale: 4 }),
    marginPercent: numeric('margin_percent', { precision: 10, scale: 4 }),
    businessUnitId: uuid('business_unit_id').references(
      () => businessUnits.id,
      { onDelete: 'set null' },
    ),
    contractId: varchar('contract_id', { length: 255 }).notNull(),
    ...timestamps,
  },
  (table) => [
    index('proposal_extensions_organization_id_idx').on(table.organizationId),
    index('proposal_extensions_owner_id_idx').on(table.ownerId),
    index('proposal_extensions_proposal_id_idx').on(table.proposalId),
    uniqueIndex('proposal_extensions_proposal_id_org_unique').on(
      table.proposalId,
      table.organizationId,
    ),
    uniqueIndex('proposal_extensions_contract_owner_org_unique').on(
      table.contractId,
      table.ownerId,
      table.organizationId,
    ),
  ],
);

export const proposalExtensionEstimators = pgTable(
  'proposal_extension_estimators',
  {
    ...id,
    proposalExtensionId: uuid('proposal_extension_id')
      .notNull()
      .references(() => proposalExtensions.id, { onDelete: 'cascade' }),
    estimatorId: uuid('estimator_id')
      .notNull()
      .references(() => estimators.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    ...timestamps,
  },
  (table) => [
    index('proposal_extension_estimators_extension_id_idx').on(
      table.proposalExtensionId,
    ),
    index('proposal_extension_estimators_estimator_id_idx').on(
      table.estimatorId,
    ),
    index('proposal_extension_estimators_organization_id_idx').on(
      table.organizationId,
    ),
    uniqueIndex('proposal_extension_estimators_extension_estimator_unique').on(
      table.proposalExtensionId,
      table.estimatorId,
    ),
  ],
);

export const proposalExtensionWorkTypes = pgTable(
  'proposal_extension_work_types',
  {
    ...id,
    proposalExtensionId: uuid('proposal_extension_id')
      .notNull()
      .references(() => proposalExtensions.id, { onDelete: 'cascade' }),
    workTypeId: uuid('work_type_id')
      .notNull()
      .references(() => workTypes.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    ...timestamps,
  },
  (table) => [
    index('proposal_extension_work_types_extension_id_idx').on(
      table.proposalExtensionId,
    ),
    index('proposal_extension_work_types_work_type_id_idx').on(
      table.workTypeId,
    ),
    index('proposal_extension_work_types_organization_id_idx').on(
      table.organizationId,
    ),
    uniqueIndex('proposal_extension_work_types_extension_type_unique').on(
      table.proposalExtensionId,
      table.workTypeId,
    ),
  ],
);

export const warehouseSyncSessionStatusEnum = pgEnum(
  'warehouse_sync_session_status',
  Object.values(WAREHOUSE_SYNC_SESSION_STATUS_VALUES) as [string, ...string[]],
);

export const warehouseSyncSessions = pgTable(
  'warehouse_sync_sessions',
  {
    ...id,
    status: warehouseSyncSessionStatusEnum('status')
      .notNull()
      .default('diffing'),
    ownerExternalIds: jsonb('owner_external_ids')
      .$type<string[]>()
      .notNull()
      .default([]),
    ownersToSync: jsonb('owners_to_sync')
      .$type<string[]>()
      .notNull()
      .default([]),
    ownersSkipped: jsonb('owners_skipped')
      .$type<string[]>()
      .notNull()
      .default([]),
    ownersCompleted: jsonb('owners_completed')
      .$type<string[]>()
      .notNull()
      .default([]),
    ownersFailed: jsonb('owners_failed')
      .$type<string[]>()
      .notNull()
      .default([]),
    currentOwnerExternalId: varchar('current_owner_external_id', {
      length: 255,
    }),
    diffResults: jsonb('diff_results'),
    validationResults: jsonb('validation_results'),
    errorMessage: text('error_message'),
    startedAt: timestamp('started_at', { mode: 'date' }),
    diffCompletedAt: timestamp('diff_completed_at', { mode: 'date' }),
    syncCompletedAt: timestamp('sync_completed_at', { mode: 'date' }),
    completedAt: timestamp('completed_at', { mode: 'date' }),
    ...timestamps,
  },
  (table) => [
    index('warehouse_sync_sessions_status_idx').on(table.status),
    index('warehouse_sync_sessions_created_at_idx').on(table.createdAt),
  ],
);

export const organizationVendors = pgTable(
  'organization_vendors',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'cascade' })
      .notNull(),
    vendorId: uuid('vendor_id')
      .notNull()
      .references(() => vendors.id, { onDelete: 'restrict' }),
    globalVendorId: uuid('global_vendor_id').references(
      () => globalVendors.id,
      { onDelete: 'set null' },
    ),
    ...timestamps,
  },
  (table) => [
    index('organization_vendors_organization_id_idx').on(table.organizationId),
    index('organization_vendors_vendor_id_idx').on(table.vendorId),
    index('organization_vendors_global_vendor_id_idx').on(table.globalVendorId),
    uniqueIndex('organization_vendors_organization_id_vendor_id_unique').on(
      table.organizationId,
      table.vendorId,
    ),
  ],
);

// Per-user, per-organization "favorite contractor" marks. A favorite is keyed
// by the cross-owner global vendor (not a single per-owner vendor row) so the
// scoreboard — which aggregates by global vendor — can flag and filter favorites
// consistently. Scoped to the org's available vendors at write time (see
// VendorFavoritesRepository).
export const vendorFavorites = pgTable(
  'vendor_favorites',
  {
    ...id,
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    globalVendorId: uuid('global_vendor_id')
      .notNull()
      .references(() => globalVendors.id, { onDelete: 'cascade' }),
    ...timestamps,
  },
  (table) => [
    index('vendor_favorites_organization_id_idx').on(table.organizationId),
    index('vendor_favorites_user_id_idx').on(table.userId),
    index('vendor_favorites_global_vendor_id_idx').on(table.globalVendorId),
    uniqueIndex('vendor_favorites_org_user_global_vendor_unique').on(
      table.organizationId,
      table.userId,
      table.globalVendorId,
    ),
  ],
);

export const warehouseSyncJobStatusEnum = pgEnum(
  'warehouse_sync_job_status',
  Object.values(WAREHOUSE_SYNC_JOB_STATUS_VALUES) as [string, ...string[]],
);

export const warehouseSyncJobs = pgTable(
  'warehouse_sync_jobs',
  {
    ...id,
    sessionId: uuid('session_id').references(() => warehouseSyncSessions.id, {
      onDelete: 'set null',
    }),
    ownerExternalId: varchar('owner_external_id', { length: 255 }).notNull(),
    userId: uuid('user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    status: warehouseSyncJobStatusEnum('status').notNull().default('pending'),
    currentTable: varchar('current_table', { length: 50 }),
    completedTables: jsonb('completed_tables').$type<string[]>().default([]),
    tableCounts: jsonb('table_counts').$type<Record<string, number>>(),
    syncedCounts: jsonb('synced_counts')
      .$type<Record<string, { synced: number; errors: number }>>()
      .default({}),
    errorMessage: text('error_message'),
    failedTable: varchar('failed_table', { length: 50 }),
    failedOffset: integer('failed_offset'),
    qstashJobIds: jsonb('qstash_job_ids').$type<string[]>().default([]),
    validationResults: jsonb('validation_results').$type<{
      snowflakeCounts: Record<string, number>;
      postgresqlCounts: Record<string, number>;
      mismatches: string[];
      isValid: boolean;
    }>(),
    // Last letting date synced (from the most recent letting in the data)
    lastLettingDate: timestamp('last_letting_date', { mode: 'date' }),
    // Forecast ID remap snapshots — persisted between QStash steps
    remapSnapshot: jsonb('remap_snapshot').$type<{
      vendorIds?: Array<{
        config_id: string;
        old_vendor_uuid: string;
        vendor_external_id: string | null;
      }>;
      bidExternalIds?: Array<{
        config_id: string;
        source_table: string;
        old_external_id: string;
        vendor_external_id: string | null;
        proposal_external_id: string | null;
      }>;
      vendorDrift?: Array<{
        oldExternalId: string;
        newExternalId: string;
      }>;
    }>(),
    // Memory metrics - atomic updates during batch processing
    peakMemoryMb: numeric('peak_memory_mb', { precision: 10, scale: 2 }),
    totalDurationMs: integer('total_duration_ms'),
    batchCount: integer('batch_count'),
    startedAt: timestamp('started_at', { mode: 'date' }),
    completedAt: timestamp('completed_at', { mode: 'date' }),
    ...timestamps,
  },
  (table) => [
    index('warehouse_sync_jobs_user_id_idx').on(table.userId),
    index('warehouse_sync_jobs_status_idx').on(table.status),
    index('warehouse_sync_jobs_created_at_idx').on(table.createdAt),
  ],
);

export const purgeExceptions = pgTable(
  'purge_exceptions',
  {
    ...id,
    purgeRunId: uuid('purge_run_id').notNull(),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    tableName: varchar('table_name', { length: 255 }).notNull(),
    columnName: varchar('column_name', { length: 255 }).notNull(),
    referencedTable: varchar('referenced_table', { length: 255 }).notNull(),
    action: varchar('action', { length: 50 }).notNull(),
    affectedCount: integer('affected_count').notNull(),
    details: text('details'),
    affectedEntityIds: jsonb('affected_entity_ids').$type<
      { id: string; label: string }[]
    >(),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    acknowledgedAt: timestamp('acknowledged_at'),
    acknowledgedBy: uuid('acknowledged_by').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (table) => [
    index('purge_exceptions_purge_run_id_idx').on(table.purgeRunId),
    index('purge_exceptions_organization_id_idx').on(table.organizationId),
  ],
);

export const purgeRuns = pgTable(
  'purge_runs',
  {
    ...id,
    status: varchar('status', { length: 32 }).notNull().default('running'),
    totals: jsonb('totals').$type<Record<string, number>>().notNull(),
    grandTotal: integer('grand_total').notNull(),
    totalChunks: integer('total_chunks').notNull(),
    deletedSoFar: integer('deleted_so_far').notNull().default(0),
    error: text('error'),
    startedAt: timestamp('started_at').notNull().defaultNow(),
    completedAt: timestamp('completed_at'),
  },
  (table) => [index('purge_runs_started_at_idx').on(table.startedAt)],
);

export const purgeRunChunks = pgTable(
  'purge_run_chunks',
  {
    ...id,
    runId: uuid('run_id')
      .notNull()
      .references(() => purgeRuns.id, { onDelete: 'cascade' }),
    tableName: varchar('table_name', { length: 64 }).notNull(),
    pageStart: integer('page_start').notNull(),
    pageEnd: integer('page_end').notNull(),
    sequence: integer('sequence').notNull(),
    status: varchar('status', { length: 32 }).notNull().default('pending'),
    deletedCount: integer('deleted_count').notNull().default(0),
    error: text('error'),
    startedAt: timestamp('started_at'),
    completedAt: timestamp('completed_at'),
    createdAt: timestamp('created_at').notNull().defaultNow(),
  },
  (table) => [
    index('purge_run_chunks_run_id_idx').on(table.runId),
    index('purge_run_chunks_run_seq_idx').on(table.runId, table.sequence),
  ],
);

// ── Item Collections ────────────────────────────────────────────────────────

export const itemCollections = pgTable(
  'item_collections',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    code: varchar('code', { length: 20 }).unique(),
    name: varchar('name', { length: 255 }).notNull(),
    targetUnitId: uuid('target_unit_id'),
    source: varchar('source', { length: 20 }).notNull().default('default'),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('item_collections_external_id_unique').on(table.externalId),
    index('item_collections_deleted_at_idx')
      .on(table.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

export const itemCollectionPlatformOverrides = pgTable(
  'item_collection_platform_overrides',
  {
    collectionId: uuid('collection_id')
      .primaryKey()
      .references(() => itemCollections.id, { onDelete: 'restrict' }),
    name: text('name'),
    isActive: boolean('is_active'),
    targetUnitId: uuid('target_unit_id'),
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
);

export const itemCollectionOrgOverrides = pgTable(
  'item_collection_org_overrides',
  {
    collectionId: uuid('collection_id')
      .notNull()
      .references(() => itemCollections.id, { onDelete: 'restrict' }),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    name: text('name'),
    isActive: boolean('is_active'),
    targetUnitId: uuid('target_unit_id'),
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
  (t) => [
    uniqueIndex('item_coll_org_overrides_unique').on(
      t.collectionId,
      t.organizationId,
    ),
  ],
);

export const itemCollectionMappings = pgTable(
  'item_collection_mappings',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    collectionId: uuid('collection_id')
      .notNull()
      .references(() => itemCollections.id),
    itemId: uuid('item_id')
      .notNull()
      .references(() => items.id, { onDelete: 'cascade' }),
    sourceUom: varchar('source_uom', { length: 50 }).notNull(),
    sourceUnitId: uuid('source_unit_id').references(() => units.id, {
      onDelete: 'set null',
    }),
    conversionFactor: numeric('conversion_factor', {
      precision: 20,
      scale: 10,
    }).notNull(),
    mappingSource: varchar('mapping_source', { length: 20 }).notNull(),
    ...timestamps,
    ...syncTracking,
  },
  (t) => [
    uniqueIndex('item_coll_mappings_unique').on(t.collectionId, t.itemId),
    index('item_coll_mappings_collection_idx').on(t.collectionId),
    index('item_coll_mappings_item_idx').on(t.itemId),
    uniqueIndex('item_coll_mappings_external_id_unique').on(t.externalId),
    index('item_coll_mappings_deleted_at_idx')
      .on(t.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

export const itemCollectionMappingPlatformOverrides = pgTable(
  'item_collection_mapping_platform_overrides',
  {
    collectionId: uuid('collection_id')
      .notNull()
      .references(() => itemCollections.id),
    itemId: uuid('item_id')
      .notNull()
      .references(() => items.id, { onDelete: 'restrict' }),
    sourceUom: varchar('source_uom', { length: 50 }),
    sourceUnitId: uuid('source_unit_id').references(() => units.id, {
      onDelete: 'set null',
    }),
    conversionFactor: numeric('conversion_factor', {
      precision: 20,
      scale: 10,
    }),
    isActive: boolean('is_active'),
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
  (t) => [
    uniqueIndex('item_coll_map_plat_unique').on(t.collectionId, t.itemId),
  ],
);

export const itemCollectionMappingOrgOverrides = pgTable(
  'item_collection_mapping_org_overrides',
  {
    collectionId: uuid('collection_id')
      .notNull()
      .references(() => itemCollections.id),
    itemId: uuid('item_id')
      .notNull()
      .references(() => items.id, { onDelete: 'restrict' }),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    sourceUom: varchar('source_uom', { length: 50 }),
    sourceUnitId: uuid('source_unit_id').references(() => units.id, {
      onDelete: 'set null',
    }),
    conversionFactor: numeric('conversion_factor', {
      precision: 20,
      scale: 10,
    }),
    isActive: boolean('is_active'),
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
  (t) => [
    uniqueIndex('item_coll_map_org_unique').on(
      t.collectionId,
      t.itemId,
      t.organizationId,
    ),
    index('item_coll_map_org_org_idx').on(t.organizationId),
  ],
);

// Precomputed per-(org, collection, proposal) physical quantity with the
// 3-layer factor override (org → platform → warehouse) already resolved.
// Refreshed by item-collections refresh service; read by scoreboard and the
// upcoming Owner Funding Report's "by item collection" cut.
export const proposalCollectionQuantity = pgTable(
  'proposal_collection_quantity',
  {
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    collectionId: uuid('collection_id')
      .notNull()
      .references(() => itemCollections.id, { onDelete: 'cascade' }),
    proposalId: uuid('proposal_id')
      .notNull()
      .references(() => proposals.id, { onDelete: 'cascade' }),
    quantity: numeric('quantity', { precision: 20, scale: 10 }).notNull(),
    ...timestamps,
  },
  (t) => [
    primaryKey({
      columns: [t.organizationId, t.collectionId, t.proposalId],
      name: 'proposal_collection_quantity_pkey',
    }),
    index('proposal_collection_quantity_proposal_idx').on(
      t.proposalId,
      t.collectionId,
    ),
  ],
);

// Award dollars pre-aggregated from bid_items to (org, bid, category) grain.
// The funding/profile "category breakdown" widgets (owner funding report,
// vendor profile, and both their drill dialogs) would otherwise scan the 8M+
// row bid_items table per request and resolve each line item's category through
// the override chain on the fly — slow, and it spawns parallel workers that
// exhaust the dev container's /dev/shm (error 53100). This rollup bakes that
// scan once: each row is one winning-or-losing bid's extension summed for one
// resolved category. Reads seek it by owner (funding) or vendor (profile) and
// group by year/category, never touching bid_items.
//
// Grain is BID-level (not winning-proposal-level) because the vendor profile
// needs every bid the vendor placed — won AND lost (its "bid value" column) —
// while the owner report filters to winners. `category_id` is NOT NULL with a
// sentinel UUID (BID_CATEGORY_AWARD_UNCATEGORIZED) for items that resolve to no
// category, so it can sit in the primary key (a real null can't). owner_id /
// vendor_id / global_vendor_id / year are denormalized so reads index-seek the
// rollup directly and only join bids/proposals back when a filter needs them.
// Refreshed like proposal_collection_quantity: scoped INSERT…ON CONFLICT off
// the request path (warehouse sync per owner, category-override changes).
export const bidCategoryAward = pgTable(
  'bid_category_award',
  {
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    bidId: uuid('bid_id')
      .notNull()
      .references(() => bids.id, { onDelete: 'cascade' }),
    // Resolved category (org override → platform override → base), or the
    // uncategorized sentinel. No FK: the sentinel has no item_categories row.
    categoryId: uuid('category_id').notNull(),
    ownerId: uuid('owner_id').notNull(),
    vendorId: uuid('vendor_id').notNull(),
    globalVendorId: uuid('global_vendor_id'),
    // Calendar year of the bid's letting — denormalized so the breakdown groups
    // by year without joining lettings.
    year: integer('year').notNull(),
    winner: boolean('winner').notNull(),
    extension: numeric('extension', { precision: 20, scale: 10 }).notNull(),
    ...timestamps,
  },
  (t) => [
    primaryKey({
      columns: [t.organizationId, t.bidId, t.categoryId],
      name: 'bid_category_award_pkey',
    }),
    // Owner funding report: seek the org's winning awards for one owner.
    index('bid_category_award_owner_idx').on(
      t.organizationId,
      t.ownerId,
      t.winner,
    ),
    // Vendor profile: seek by normalized or local vendor identity.
    index('bid_category_award_vendor_idx').on(t.organizationId, t.vendorId),
    index('bid_category_award_global_vendor_idx').on(
      t.organizationId,
      t.globalVendorId,
    ),
    // Per-bid lookup: the vendor drill's category_value lateral, and the bids
    // FK cascade.
    index('bid_category_award_bid_idx').on(t.bidId),
    // Category-award rollup (owner funding / category breakdown): a covering
    // index over the org's winning awards. Keyed by (org, owner, year, category)
    // so the report's filter and GROUP BY/ORDER BY stream straight from the
    // index; bid_id + extension trail as payload so the scan stays index-only
    // (no heap fetch). Partial on `winner` — the rollup only reads winning rows.
    index('bid_category_award_winner_rollup_idx')
      .on(
        t.organizationId,
        t.ownerId,
        t.year,
        t.categoryId,
        t.bidId,
        t.extension,
      )
      .where(sql`winner`),
  ],
);

// ── Item Categories ─────────────────────────────────────────────────────────

export const itemCategories = pgTable(
  'item_categories',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    code: varchar('code', { length: 50 }).unique(),
    name: varchar('name', { length: 255 }).notNull(),
    source: varchar('source', { length: 20 }).notNull().default('default'),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('item_categories_external_id_unique').on(table.externalId),
    index('item_categories_deleted_at_idx')
      .on(table.deletedAt)
      .where(sql`deleted_at IS NOT NULL`),
  ],
);

export const itemCategoryPlatformOverrides = pgTable(
  'item_category_platform_overrides',
  {
    categoryId: uuid('category_id')
      .primaryKey()
      .references(() => itemCategories.id, { onDelete: 'restrict' }),
    name: text('name'),
    isActive: boolean('is_active'),
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
);

export const itemCategoryOrgOverrides = pgTable(
  'item_category_org_overrides',
  {
    categoryId: uuid('category_id')
      .notNull()
      .references(() => itemCategories.id, { onDelete: 'restrict' }),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    name: text('name'),
    isActive: boolean('is_active'),
    isPriceLinked: boolean('is_price_linked'),
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
  (t) => [
    uniqueIndex('item_cat_org_overrides_unique').on(
      t.categoryId,
      t.organizationId,
    ),
  ],
);

// ── Item Property Overrides (category assignment + lump sum per item) ───

export const itemPlatformOverrides = pgTable('item_platform_overrides', {
  itemId: uuid('item_id')
    .primaryKey()
    .references(() => items.id, { onDelete: 'restrict' }),
  categoryId: uuid('category_id').references(() => itemCategories.id, {
    onDelete: 'set null',
  }),
  isTrueLumpSum: boolean('is_true_lump_sum'),
  // ── Liquid AC item config (ADR-0109 / EDG-976). NULL = no override at this
  // tier; resolution falls through field by field to the warehouse/spec values
  // on bid_item_liquid_ac. conversion_factor here is the ADR's "item override"
  // grain, consumed ONLY by the Liquid AC engine ahead of the collection
  // mapping chain — asphalt tonnage rollups keep reading the mapping factor,
  // so an unmapped item never joins tonnage through this field.
  needsLiquidAsphaltAdjustment: boolean('needs_liquid_asphalt_adjustment'),
  liquidAcPattern: liquidAcPatternEnum('liquid_ac_pattern'),
  pgGrade: varchar('pg_grade', { length: 50 }),
  liquidAcPct: numeric('liquid_ac_pct', { precision: 6, scale: 4 }),
  conversionFactor: numeric('conversion_factor', { precision: 20, scale: 10 }),
  updatedBy: uuid('updated_by').references(() => users.id),
  ...timestamps,
});

export const itemOrgOverrides = pgTable(
  'item_org_overrides',
  {
    itemId: uuid('item_id')
      .notNull()
      .references(() => items.id, { onDelete: 'restrict' }),
    organizationId: uuid('organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    categoryId: uuid('category_id').references(() => itemCategories.id, {
      onDelete: 'set null',
    }),
    isTrueLumpSum: boolean('is_true_lump_sum'),
    isPriceLinked: boolean('is_price_linked'),
    // ── Liquid AC item config (ADR-0109 / EDG-976). Same field-level
    // fallthrough semantics as the platform tier; org tier wins.
    needsLiquidAsphaltAdjustment: boolean('needs_liquid_asphalt_adjustment'),
    liquidAcPattern: liquidAcPatternEnum('liquid_ac_pattern'),
    pgGrade: varchar('pg_grade', { length: 50 }),
    liquidAcPct: numeric('liquid_ac_pct', { precision: 6, scale: 4 }),
    conversionFactor: numeric('conversion_factor', {
      precision: 20,
      scale: 10,
    }),
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
  (t) => [
    uniqueIndex('item_org_overrides_unique').on(t.itemId, t.organizationId),
    index('item_org_overrides_org_idx').on(t.organizationId),
  ],
);

/**
 * Per-organization Liquid AC on/off switch. One row per org; `enabled` is the
 * operational gate the forecast pipeline consults via lookup (layered AND with
 * the `liquid-ac-adjustment` PostHog availability flag). No row = enabled (ON).
 */
export const organizationLiquidAsphalt = pgTable(
  'organization_liquid_asphalt',
  {
    organizationId: uuid('organization_id')
      .primaryKey()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    enabled: boolean('enabled').notNull().default(true),
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
);

/**
 * Append-only audit trail for Liquid AC item-config edits (EDG-976).
 * One row per changed field per edit: actor, prior value, new value, timestamp.
 * organization_id NULL = platform-tier edit (operations); set = org-tier edit
 * (tenant-scoped via RLS). Values are stored as text snapshots of the field.
 */
export const itemLiquidAcConfigAudits = pgTable(
  'item_liquid_ac_config_audits',
  {
    ...id,
    itemId: uuid('item_id')
      .notNull()
      .references(() => items.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id').references(() => organizations.id, {
      onDelete: 'cascade',
    }),
    changedBy: uuid('changed_by')
      .notNull()
      .references(() => users.id),
    field: varchar('field', { length: 64 }).notNull(),
    priorValue: text('prior_value'),
    newValue: text('new_value'),
    ...timestamps,
  },
  (t) => [
    index('item_liquid_ac_config_audits_item_idx').on(t.itemId, t.createdAt),
    index('item_liquid_ac_config_audits_org_idx').on(t.organizationId),
  ],
);

// ── Forecast Bid Exclusions ──────────────────────────────────────────────────

export const forecastBidExclusions = pgTable(
  'forecast_bid_exclusions',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`. The forecast_config_id FK
    // is out of scope (not a forecast_id -> forecasts link).
    forecastId: uuid('forecast_id').notNull(),
    forecastConfigId: uuid('forecast_config_id').references(
      () => forecastConfigs.id,
      { onDelete: 'cascade' },
    ),
    itemNumber: varchar('item_number', { length: 255 }).notNull(),
    bidExternalId: varchar('bid_external_id', { length: 255 }).notNull(),
    excludedLineNumbers: integer('excluded_line_numbers').array(),
    vendorExternalId: varchar('vendor_external_id', { length: 255 }),
    proposalExternalId: varchar('proposal_external_id', { length: 255 }),
    unitPriceAtExclusion: numeric('unit_price_at_exclusion', {
      precision: 20,
      scale: 10,
    }),
    excludedAt: timestamp('excluded_at', { mode: 'date' })
      .notNull()
      .defaultNow(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    ...timestamps,
  },
  (table) => [
    index('forecast_bid_exclusions_forecast_id_idx').on(table.forecastId),
    index('forecast_bid_exclusions_config_id_idx').on(table.forecastConfigId),
    index('forecast_bid_exclusions_org_id_idx').on(table.organizationId),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180). Replaces the simple single-column forecast_id FK. ON DELETE
    // cascade is preserved.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_bid_exclusions_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

// Bids the user has excluded from a vendor's sensitivity analysis. Stored per
// (organization, global vendor) — NOT per user — so the exclusion is shared and
// persists. An excluded bid is dropped from the analysis (series, KPIs, totals)
// but still shown in the tables (faded) so it can be restored.
export const vendorSensitivityBidExclusions = pgTable(
  'vendor_sensitivity_bid_exclusions',
  {
    ...id,
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    globalVendorId: uuid('global_vendor_id')
      .references(() => globalVendors.id, { onDelete: 'cascade' })
      .notNull(),
    bidId: uuid('bid_id')
      .references(() => bids.id, { onDelete: 'cascade' })
      .notNull(),
    excludedAt: timestamp('excluded_at', { mode: 'date' })
      .notNull()
      .defaultNow(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('vendor_sensitivity_bid_exclusions_unique').on(
      table.organizationId,
      table.globalVendorId,
      table.bidId,
    ),
    index('vendor_sensitivity_bid_exclusions_org_vendor_idx').on(
      table.organizationId,
      table.globalVendorId,
    ),
  ],
);

// ── Slack integration ────────────────────────────────────────────────────────
// One row per Slack workspace that has installed the Edgevanta app. The
// bot_token is workspace-scoped and stored encrypted at rest (envelope
// encryption via app-level key derived from SLACK_TOKEN_ENCRYPTION_KEY).
export const slackWorkspaces = pgTable(
  'slack_workspaces',
  {
    ...id,
    teamId: varchar('team_id', { length: 64 }).notNull().unique(),
    teamName: varchar('team_name', { length: 255 }),
    botUserId: varchar('bot_user_id', { length: 64 }).notNull(),
    botTokenEncrypted: text('bot_token_encrypted').notNull(),
    installedByUserId: uuid('installed_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    defaultOrganizationId: uuid('default_organization_id').references(
      () => organizations.id,
      { onDelete: 'set null' },
    ),
    ...timestamps,
  },
  (table) => [index('slack_workspaces_team_id_idx').on(table.teamId)],
);

// One row per (Slack user, Edgevanta user) pair. A single Edgevanta user
// can be linked from multiple Slack workspaces. Lookup key is
// (team_id, slack_user_id) — that's what arrives on every Slack event.
export const slackUserLinks = pgTable(
  'slack_user_links',
  {
    ...id,
    teamId: varchar('team_id', { length: 64 }).notNull(),
    slackUserId: varchar('slack_user_id', { length: 64 }).notNull(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    defaultOrganizationId: uuid('default_organization_id')
      .notNull()
      .references(() => organizations.id, { onDelete: 'cascade' }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('slack_user_links_team_slack_user_unique').on(
      table.teamId,
      table.slackUserId,
    ),
    index('slack_user_links_user_id_idx').on(table.userId),
  ],
);

// ── Units ────────────────────────────────────────────────────────────────────

export const units = pgTable(
  'units',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    code: varchar('code', { length: 50 }).notNull().unique(),
    fullName: varchar('full_name', { length: 255 }),
    isCollectionEligible: boolean('is_collection_eligible')
      .notNull()
      .default(false),
    variants: text('variants').array(),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [uniqueIndex('units_external_id_unique').on(table.externalId)],
);

// ── Global proposal classification dims ─────────────────────────────────────
// Platform-managed and GLOBAL (no organization_id) — the warehouse classifies
// every proposal onto both axes, so these are shared vocabulary, not org tags.
// The per-org, user-authored tags are `work_types`; do not confuse the two.
// `external_id` is the warehouse uid, minted from the stable `code` and never
// the display name, so renaming a label cannot drift ids.

export const contractTypes = pgTable(
  'contract_types',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    code: varchar('code', { length: 50 }).notNull().unique(),
    name: varchar('name', { length: 255 }).notNull(),
    // How the award is decided (e.g. low bid vs best value). Carried on the dim
    // rather than the proposal — it is a property of the contract type.
    awardBasis: varchar('award_basis', { length: 50 }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('contract_types_external_id_unique').on(table.externalId),
  ],
);

export const projectTypes = pgTable(
  'project_types',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }),
    code: varchar('code', { length: 50 }).notNull().unique(),
    name: varchar('name', { length: 255 }).notNull(),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('project_types_external_id_unique').on(table.externalId),
  ],
);

// ── Liquid Asphalt Adjustment (EDG-979; v2 contract EDG-1273 / ADR-0109) ─────

export const liquidAcIndexes = pgTable(
  'liquid_ac_indexes',
  {
    ...id,
    externalId: varchar('external_id', { length: 255 }).notNull(),
    state: varchar('state', { length: 10 }).notNull(),
    grade: varchar('grade', { length: 50 }).notNull(),
    region: varchar('region', { length: 50 }),
    monthDate: timestamp('month_date', { mode: 'date' }).notNull(),
    pricePerTonUsd: numeric('price_per_ton_usd', {
      precision: 12,
      scale: 4,
    }).notNull(),
    publishedUnit: varchar('published_unit', { length: 32 }),
    samples: integer('samples'),
    isProxy: boolean('is_proxy').notNull().default(false),
    proxySources: varchar('proxy_sources', { length: 255 }),
    // EDG-977 provenance. publicationDate is when the value was published /
    // retrieved (NULL for pre-provenance backfill rows — staleness then falls
    // back to monthDate). revision is the latest warehouse revision for this
    // series-month: corrections are appended warehouse-side and synced in
    // place here under the same external_id; history stays in the warehouse.
    publicationDate: timestamp('publication_date', { mode: 'date' }),
    revision: integer('revision').notNull().default(1),
    sourceKey: varchar('source_key', { length: 100 }),
    sourceUrl: varchar('source_url', { length: 500 }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('liquid_ac_indexes_external_id_unique').on(table.externalId),
    index('liquid_ac_indexes_state_grade_month_idx').on(
      table.state,
      table.grade,
      table.monthDate,
    ),
  ],
);

export const liquidAcStateConfigs = pgTable(
  'liquid_ac_state_configs',
  {
    ...id,
    state: varchar('state', { length: 10 }).notNull(),
    hasProgram: boolean('has_program').notNull().default(false),
    indexPgGrade: varchar('index_pg_grade', { length: 50 }),
    thresholdPct: numeric('threshold_pct', { precision: 6, scale: 4 }),
    baseIndexAnchor: varchar('base_index_anchor', { length: 32 }),
    hasZones: boolean('has_zones').default(false),
    publishCadence: varchar('publish_cadence', { length: 32 }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('liquid_ac_state_configs_state_unique').on(table.state),
  ],
);

export const bidItemLiquidAc = pgTable(
  'bid_item_liquid_ac',
  {
    ...id,
    bidItemId: uuid('bid_item_id')
      .notNull()
      .references(() => bidItems.id, { onDelete: 'cascade' }),
    state: varchar('state', { length: 10 }).notNull(),
    pattern: varchar('pattern', { length: 32 }).notNull(),
    mixFamily: varchar('mix_family', { length: 32 }),
    liquidAcPct: numeric('liquid_ac_pct', { precision: 6, scale: 4 }),
    pgGrade: varchar('pg_grade', { length: 50 }),
    needsLiquidAdjustment: boolean('needs_liquid_adjustment')
      .notNull()
      .default(false),
    historicalIndexAtBid: numeric('historical_index_at_bid', {
      precision: 12,
      scale: 4,
    }),
    historicalIndexGrade: varchar('historical_index_grade', { length: 50 }),
    historicalIndexRegion: varchar('historical_index_region', { length: 50 }),
    historicalIndexIsProxy: boolean('historical_index_is_proxy'),
    basis: numeric('basis', { precision: 12, scale: 4 }),
    shouldAdjust: boolean('should_adjust').notNull().default(false),
    adjustmentWarning: varchar('adjustment_warning', { length: 64 }),
    // ── Canonical v2 contract (ADR-0109 / EDG-1273). Additive, nullable, dual-write. ──
    // Configuration (denormalized onto the bid-line snapshot at classification time).
    liquidAcPattern: liquidAcPatternEnum('liquid_ac_pattern'),
    needsLiquidAsphaltAdjustment: boolean('needs_liquid_asphalt_adjustment'),
    classificationSource: varchar('classification_source', { length: 32 }),
    classificationConfidence: numeric('classification_confidence', {
      precision: 6,
      scale: 4,
    }),
    liquidAcSpecRuleId: varchar('liquid_ac_spec_rule_id', { length: 255 }),
    liquidAcSpecVersion: varchar('liquid_ac_spec_version', { length: 64 }),
    effectiveFrom: timestamp('effective_from', { mode: 'date' }),
    effectiveTo: timestamp('effective_to', { mode: 'date' }),
    // Runtime result (derived snapshot). adjustment_status replaces should_adjust.
    adjustmentStatus: adjustmentStatusEnum('adjustment_status'),
    adjustmentWarnings: text('adjustment_warnings').array(),
    // Immutable derivation inputs / provenance for the historical bid row.
    requestedItemGrade: varchar('requested_item_grade', { length: 50 }),
    resolvedIndexGrade: varchar('resolved_index_grade', { length: 50 }),
    gradeFallbackUsed: boolean('grade_fallback_used'),
    gradeFallbackReason: varchar('grade_fallback_reason', { length: 64 }),
    indexAtBidRowId: varchar('index_at_bid_row_id', { length: 255 }),
    indexAtBidMonth: timestamp('index_at_bid_month', { mode: 'date' }),
    indexAtBidSource: varchar('index_at_bid_source', { length: 64 }),
    conversionFactorSnapshot: numeric('conversion_factor_snapshot', {
      precision: 20,
      scale: 10,
    }),
    ...timestamps,
    ...syncTracking,
  },
  (table) => [
    uniqueIndex('bid_item_liquid_ac_bid_item_id_unique').on(table.bidItemId),
    index('bid_item_liquid_ac_pattern_idx').on(table.pattern),
    index('bid_item_liquid_ac_state_idx').on(table.state),
    index('bid_item_liquid_ac_liquid_ac_pattern_idx').on(table.liquidAcPattern),
  ],
);

/**
 * Frozen "index today" context per forecast (EDG-1304 / LAC-004). One row per
 * (forecast, state, grade): the index the forecast's adjustments resolve
 * against, captured once at full recalculation. Values are COPIED, never
 * referenced — the snapshot must reproduce the forecast's adjusted prices even
 * if the source index row is later revised or soft-deleted. All rows of one
 * forecast share one `as_of` instant. Refreshed only by a full recalculation
 * (one atomic statement: prune vanished pairs + upsert re-resolved rows);
 * partial recomputes reuse the existing context.
 */
export const forecastLiquidAcSnapshots = pgTable(
  'forecast_liquid_ac_snapshots',
  {
    ...id,
    // Composite FK to forecasts (pinning parent org == row org) is declared in
    // the table-config callback below (EDG-1180), so this column intentionally
    // carries no inline single-column `.references()`.
    forecastId: uuid('forecast_id').notNull(),
    organizationId: uuid('organization_id')
      .references(() => organizations.id, { onDelete: 'restrict' })
      .notNull(),
    // Lookup key — mirrors the engine's live LATERAL:
    // (state, COALESCE(resolved_index_grade, historical_index_grade)).
    state: varchar('state', { length: 10 }).notNull(),
    grade: varchar('grade', { length: 50 }).notNull(),
    // Provenance of the resolved liquid_ac_indexes row. indexRowId is the
    // platform uuid at freeze time; indexExternalId is the warehouse-stable id
    // (same convention as bid_item_liquid_ac.index_at_bid_row_id). Plain
    // columns, no FK: audit pointers, not integrity constraints.
    indexRowId: uuid('index_row_id'),
    indexExternalId: varchar('index_external_id', { length: 255 }),
    region: varchar('region', { length: 50 }),
    monthDate: timestamp('month_date', { mode: 'date' }).notNull(),
    pricePerTonUsd: numeric('price_per_ton_usd', {
      precision: 12,
      scale: 4,
    }).notNull(),
    isProxy: boolean('is_proxy').notNull().default(false),
    // Latest publication was 60+ days older than as_of when resolved.
    staleIndexUsed: boolean('stale_index_used').notNull().default(false),
    // EDG-977 provenance of the frozen row: publication date + warehouse
    // revision as resolved, so a later correction is distinguishable from
    // what this forecast actually used. NULL on pre-provenance snapshots.
    publicationDate: timestamp('publication_date', { mode: 'date' }),
    revision: integer('revision'),
    // The frozen (state, grade) series had multiple published zone/region
    // series and the zone was picked deterministically, not resolved from
    // project location (EDG-977; real zone resolution is a follow-up).
    zoneFallbackUsed: boolean('zone_fallback_used').notNull().default(false),
    // 'latest_at_compute' | 'migration_backfill'
    resolutionStrategy: varchar('resolution_strategy', {
      length: 32,
    }).notNull(),
    asOf: timestamp('as_of', { mode: 'date' }).notNull(),
    ...timestamps,
  },
  (table) => [
    index('forecast_liquid_ac_snapshots_forecast_id_idx').on(table.forecastId),
    index('forecast_liquid_ac_snapshots_organization_id_idx').on(
      table.organizationId,
    ),
    uniqueIndex('forecast_liquid_ac_snapshots_forecast_state_grade_unique').on(
      table.forecastId,
      table.state,
      table.grade,
    ),
    // Composite FK pinning the parent forecast's org to this row's org
    // (EDG-1180 pattern). ON DELETE cascade: snapshots die with the forecast.
    foreignKey({
      columns: [table.forecastId, table.organizationId],
      foreignColumns: [forecasts.id, forecasts.organizationId],
      name: 'forecast_liquid_ac_snapshots_forecast_id_organization_id_fk',
    }).onDelete('cascade'),
  ],
);

export const liquidAcSpecRuleSourceEnum = pgEnum(
  'liquid_ac_spec_rule_source',
  LIQUID_AC_SPEC_RULE_SOURCE_VALUES,
);

/**
 * Owner (state DOT) spec-book editions — the versioned source documents that
 * temporal liquid-AC spec rules cite (EDG-978 / LAC-010). Platform-global like
 * liquid_ac_indexes: no organization_id; tenant is SELECT-only via RLS.
 * Created exclusively by the extraction CLI (find-or-create by state+version);
 * re-extracting a book REPLACES its rules.
 */
export const liquidAcSpecBooks = pgTable(
  'liquid_ac_spec_books',
  {
    ...id,
    state: varchar('state', { length: 10 }).notNull(),
    version: varchar('version', { length: 128 }).notNull(),
    sourceUrl: varchar('source_url', { length: 500 }),
    sourceDocument: varchar('source_document', { length: 500 }),
    recordedAt: timestamp('recorded_at', { mode: 'date' })
      .notNull()
      .defaultNow(),
    createdBy: uuid('created_by').references(() => users.id),
    ...timestamps,
  },
  (table) => [
    uniqueIndex('liquid_ac_spec_books_state_version_unique').on(
      table.state,
      table.version,
    ),
  ],
);

/**
 * Temporal liquid-AC spec rules (EDG-978 / LAC-010), effective-on-persist:
 * every row in this table IS the classification truth for its item and
 * window — there is no review status or approval gate. Rows are created only
 * by spec-book extraction (which replaces a book's whole rule set on re-run)
 * and may be point-edited or deleted by operations afterwards; updated_by
 * marks human touches so a re-extraction can warn before wiping them.
 * Effective window is INCLUSIVE of effective_to; NULL = open-ended.
 * Overlapping windows on one (state, item_number_normalized) are rejected at
 * write time — that is what keeps by-letting-date resolution unambiguous.
 * History/replay lives in the exported dbt seed (append-only revisions with
 * tombstones), not here.
 */
export const liquidAcSpecRules = pgTable(
  'liquid_ac_spec_rules',
  {
    ...id,
    specBookId: uuid('spec_book_id')
      .notNull()
      .references(() => liquidAcSpecBooks.id, { onDelete: 'cascade' }),
    // Denormalized from the spec book; service layer keeps them consistent.
    state: varchar('state', { length: 10 }).notNull(),
    itemNumber: varchar('item_number', { length: 100 }).notNull(),
    // normalizeItemNumber() output — parity with the dbt normalize_item_number
    // UDF; the warehouse joins rules to items on this.
    itemNumberNormalized: varchar('item_number_normalized', {
      length: 100,
    }).notNull(),
    effectiveFrom: timestamp('effective_from', { mode: 'date' }).notNull(),
    effectiveTo: timestamp('effective_to', { mode: 'date' }),
    // Pattern-conditional payload validation lives in the service:
    // needs=true requires a pattern AND a canonical PG grade; P1-P3 require
    // liquid_ac_pct; P4 forbids pct; needs=false forbids the whole payload.
    needsLiquidAsphaltAdjustment: boolean(
      'needs_liquid_asphalt_adjustment',
    ).notNull(),
    pattern: liquidAcPatternEnum('pattern'),
    // Constrained to PG_GRADE_VALUES by the service (kept varchar so the
    // canonical list can grow without migrations).
    pgGrade: varchar('pg_grade', { length: 50 }),
    // When the spec text states a range, this is its midpoint and the
    // verbatim evidence quote carries the printed bounds.
    liquidAcPct: numeric('liquid_ac_pct', { precision: 6, scale: 4 }),
    factorDefaultPolicy: varchar('factor_default_policy', { length: 255 }),
    sourceType: liquidAcSpecRuleSourceEnum('source_type').notNull(),
    // Evidence + extraction provenance.
    evidencePage: integer('evidence_page'),
    evidenceText: text('evidence_text'),
    extractionRunId: varchar('extraction_run_id', { length: 128 }),
    extractionModel: varchar('extraction_model', { length: 128 }),
    extractionPromptVersion: varchar('extraction_prompt_version', {
      length: 64,
    }),
    confidence: numeric('confidence', { precision: 6, scale: 4 }),
    createdBy: uuid('created_by').references(() => users.id),
    // Set on every manual edit; NULL means the row is untouched extraction
    // output. Re-extraction warns when it is about to replace edited rows.
    updatedBy: uuid('updated_by').references(() => users.id),
    ...timestamps,
  },
  (table) => [
    index('liquid_ac_spec_rules_state_item_normalized_idx').on(
      table.state,
      table.itemNumberNormalized,
    ),
    index('liquid_ac_spec_rules_spec_book_id_idx').on(table.specBookId),
  ],
);
